/* Ant lead-chat widget — vanilla, self-contained, no deps.
 *
 * Embed:
 *   <script src="/widget.js"
 *     data-api="https://api.<your-instance>.com"
 *     data-flow="<your-kb-slug>"
 *     data-tenant="<your-tenant-id>"
 *     data-accent="#FFC857"
 *     data-bg="#0F0E0D"
 *     data-poll="6000"
 *     defer></script>
 *
 * The widget reads its config from the script tag's data-* attributes.
 * Owner name + avatar come from the server response (so we don't have
 * to keep them in sync between landing page and DB).
 */
(function () {
  'use strict';

  // ─── Config ──────────────────────────────────────────────────────
  var SCRIPT = document.currentScript || (function () {
    var s = document.getElementsByTagName('script');
    return s[s.length - 1];
  })();

  var CFG = {
    api:    SCRIPT.getAttribute('data-api') || '',
    tenant: SCRIPT.getAttribute('data-tenant') || '',
    inv:    SCRIPT.getAttribute('data-inv')    || '',
    accent: SCRIPT.getAttribute('data-accent') || '#FFC857',
    bg:     SCRIPT.getAttribute('data-bg')     || '#0F0E0D',
    text:   SCRIPT.getAttribute('data-text')   || '#F0EBE2',
    poll:   parseInt(SCRIPT.getAttribute('data-poll') || '6000', 10),
    storageKey: 'antLeadChatV1:' + (SCRIPT.getAttribute('data-inv') || SCRIPT.getAttribute('data-tenant') || 'default'),
    // Visitor-supplied contact info (name + email + phone) stored
    // separately from the chat token. Versioned so we can evolve
    // the shape without colliding with old values. Scoped per-inv
    // so two instances embedded on the same page (rare) don't
    // share contact submissions.
    infoKey: 'antVisitorInfoV1:' + (SCRIPT.getAttribute('data-inv') || SCRIPT.getAttribute('data-tenant') || 'default'),
    title:  SCRIPT.getAttribute('data-title') || 'Chat',
    // First name of the instance owner — used as the "Talk to <name>"
    // CTA label. Prefer an explicit data-owner-first-name attribute on
    // the landing's widget <script> tag (matters for instances whose
    // title doesn't start with the owner's first name, e.g. "50 Shades
    // Peptides" → "Adam"). Fallback: first whitespace-delimited word
    // of data-title (works for "James for Vets" → "James", "Tania
    // Valentin" → "Tania", "Danielle Furlong" → "Danielle"). Final
    // fallback "us" so the CTA never reads "Talk to ".
    ownerFirstName:
      SCRIPT.getAttribute('data-owner-first-name')
      || ((SCRIPT.getAttribute('data-title') || '').trim().split(/\s+/)[0])
      || 'us',
    // When set, the widget skips cookie/localStorage entirely — token
    // lives in memory only, every page load is a fresh chat. Used by
    // the assisted-signup guide popup so each visit doesn't drag in
    // the prior session's history (the main landing keeps history;
    // the guide is a "quick assist" surface).
    ephemeral: SCRIPT.getAttribute('data-ephemeral') === 'true',
    // Q&A flow slug — selects which product-kb the /qa endpoint pulls
    // from. Only used in ephemeral / conversational mode. Defaults
    // No default — embedding a widget without data-flow is a config
    // error. The dynamic canned-intents fetch will fail (404 on
    // /canned-intents/) and the operator can spot it in console.
    flow: SCRIPT.getAttribute('data-flow') || '',
    // Full-window mode: panel takes the entire viewport, no floating
    // bubble. Used by the dedicated /chat.html popup window the
    // landing pops alongside the THREE enrollment tab. Combined with
    // ephemeral=true so each popup is a fresh conversation.
    fullwindow: SCRIPT.getAttribute('data-fullwindow') === 'true',
  };

  if (!CFG.api) {
    console.warn('[ant-widget] data-api attribute missing — widget disabled.');
    return;
  }

  // Skip rendering inside iframes
  try { if (window.self !== window.top) return; } catch (e) { return; }

  // ─── Runtime config (loaded from /canned-intents/<flow>) ─────────
  // Per-tenant strings the widget needs to render the bot's identity.
  // Defaults are intentionally generic — the canned-intents fetch
  // overrides them at boot for any tenant that has tenant-vars
  // configured.
  var runtimeConfig = {
    botLabel: 'the team',
    greeting: null,           // null → fall back to window.antChatBootGreeting → hardcoded default
    offTopicFallback: null,   // null → use whatever the server returns
  };
  function applyRuntimeVars(vars) {
    if (!vars || typeof vars !== 'object') return;
    if (typeof vars.botLabel === 'string' && vars.botLabel.trim()) {
      runtimeConfig.botLabel = vars.botLabel.trim();
    }
    if (typeof vars.greeting === 'string' && vars.greeting.trim()) {
      runtimeConfig.greeting = vars.greeting.trim();
    }
    if (typeof vars.offTopicFallback === 'string' && vars.offTopicFallback.trim()) {
      runtimeConfig.offTopicFallback = vars.offTopicFallback.trim();
    }
  }

  // ─── Canned intents (compliance-pinned answers) ──────────────────
  // A small set of questions are answered verbatim — no LLM round-trip
  // — because the wording has been ops/legal-approved and any drift
  // toward income claims, guarantees, or "pays for itself" framing is
  // unacceptable. Matched by keyword on the visitor's raw message.
  // Returns { intent, text, actions } or null.
  var CANNED_INTENTS = (function () {
    var FEE_TEXT =
      "It's the membership that unlocks your wholesale pricing and account access.\n\n" +
      "Without it, you'd just be buying products at standard pricing. With it, you're able to order at the lower member level and stay active inside the system.\n\n" +
      "Most people think of it like a Costco-style membership — it's what gives you access to better pricing and the back office tools.\n\n" +
      "If you're just trying the product, it keeps things simple. If you decide to keep ordering, the pricing difference typically matters more over time than the fee itself.\n\n" +
      "You'll see it included in your first order — that's what activates your account.";
    var HESITATION_PREFIX =
      "It's completely fair to ask — most people pause here the first time they see it.\n\n";
    var MONTHLY_TEXT =
      "It's tied to having an active account. The details depend on how you choose to use the system — product only or share-and-earn. If you want, someone can walk through the options with you.";
    var CONTACT_TEXT =
      "James focuses on the work — the dogs, the writing, the road. The team handles outreach.\n\nDrop your name and a way to reach you below and they'll get back to you.";
    // Enrollment URLs come from /canned-intents/<flow> (variables.retailUrl,
    // variables.wholesaleUrl) per-tenant. Empty defaults here so this
    // shared widget.js stays brand-neutral — never embed a specific
    // rep's enrollment URL in the static fallback. If canned-intents
    // hasn't loaded, the static-matcher fallback below shows empty
    // URLs, which is obviously broken and forces the operator to seed
    // canned-intents (the right configuration path).
    var URL_RETAIL    = '';
    var URL_WHOLESALE = '';
    var START_TEXT =
      "You have two options — a wholesale account called Brand Ambassador, " +
      "or a retail account where you pay full price. Brand Ambassador wins " +
      "the math for almost anyone: ~25% off every order, no quota, no " +
      "monthly minimum, no obligation to recruit. The $30/year admin fee " +
      "pays for itself on the first 3-bottle pack. Ready to sign up?";
    var START_ACTIONS = [
      { label: 'Open the enrollment', kind: 'link', url: URL_WHOLESALE },
      { label: 'Just retail (one-time purchase)', kind: 'link', url: URL_RETAIL },
    ];
    var RETAIL_TEXT =
      "Here you go: " + URL_RETAIL + "\n\nCustomer One-Time or Customer Smart Ship — pick whichever fits.";
    var WHOLESALE_TEXT =
      "Here's the enrollment link: " + URL_WHOLESALE + "\n\nThe page walks you through everything — agreement, sponsor placement, starter pack — from there.";
    var STANDARD_ACTIONS = [
      { label: 'Keep going', kind: 'dismiss' },
      { label: 'Have someone walk me through it', kind: 'handoff' },
    ];

    function any(s, arr) {
      for (var i = 0; i < arr.length; i++) {
        if (s.indexOf(arr[i]) !== -1) return true;
      }
      return false;
    }
    function isHesitant(s) {
      return any(s, [
        'why is there a fee', 'why a fee', 'this feels weird', 'feels weird',
        "don't like this", 'do not like this', 'feels off', 'feels strange',
        'why am i paying', 'why am i being charged',
      ]);
    }
    function isMonthly(s) {
      // Phrases that escalate the fee question into "is this recurring".
      // These beat the generic fee match because the answer differs.
      return any(s, [
        'every month', 'monthly fee', 'monthly charge', 'recurring fee',
        'is this a subscription', 'monthly cost', 'pay this monthly',
        'pay every month', 'pay this every',
      ]);
    }
    function isFeeQ(s) {
      return any(s, [
        'back office management fee', 'back-office management fee',
        'back office fee', 'back-office fee', 'management fee',
        'back office charge', 'back office membership',
        'what is this fee', "what's this fee",
        'what is the fee', "what's the fee",
        'what is that fee', "what's that fee",
        'explain the fee', 'about the fee', 'this fee',
        'why is there a fee', 'why a fee',
      ]);
    }
    function isContactQ(s) {
      // Anyone asking how to reach James / talk to a human goes
      // straight to the inline name+phone+email form. No "say the
      // word and I'll grab your number" prompt — present it directly.
      return any(s, [
        'how do i contact', 'how can i contact', 'how to contact',
        'how do i reach', 'how can i reach', 'how to reach',
        'how do i get in touch', 'how can i get in touch', 'get in touch',
        'get into contact', 'into contact',
        'how do i talk to', 'how can i talk to', 'talk to james',
        'speak to james', 'speak with james', 'reach james', 'contact james',
        'contact someone', 'talk to someone', 'speak to someone',
        'talk to a human', 'reach a human', 'reach someone',
        'how do i message', 'how can i message',
        'text me', 'let me know when', 'when the app launches',
        'grab my number', 'take my number', 'have my number',
        'call me', 'have someone call',
        'follow up', 'follow-up', 'followup',
      ]);
    }
    function isStartQ(s) {
      // Generic "how do I start" — bot asks retail vs wholesale.
      return any(s, [
        'how do i start', 'how do i get started', 'how to get started',
        'how do i begin', 'how do i sign up', 'how do i enroll',
        'how to enroll', 'how to sign up', 'how do i join',
        'where do i start', 'where do i begin', 'how do i buy',
        'where do i buy', 'how do i order', 'where do i order',
        'how do i register', 'how do i subscribe',
        'i want to get started', 'i want to start', 'i want to begin',
      ]);
    }
    function isRetailPick(s) {
      // Visitor has picked the retail/customer path — drop link.
      return any(s, [
        'as a customer', 'just a customer', 'be a customer', 'become a customer',
        'just want to buy', 'just buying', 'retail customer', 'retail account',
        'just retail', 'as retail', 'one-time purchase', 'just want to order',
        'i am a customer', "i'm a customer", "i am customer", "i'm customer",
      ]);
    }
    function isWholesalePick(s) {
      // Visitor has picked the BA/wholesale path — drop enrollment link.
      return any(s, [
        'brand ambassador', 'become a ba', 'become an ambassador',
        'wholesale', 'wholesale account', 'sign me up',
        'sign up as ba', 'sign up as a brand', 'enroll as a brand',
        'enroll as ba', 'i want to enroll', 'i want to sign up as',
        "i'm a brand ambassador", "i am a brand ambassador",
        'i want to be a brand ambassador', 'i want to be an ambassador',
      ]);
    }

    // Server-fetched intents (loaded at boot via /canned-intents/:flow)
    // override the hardcoded matchers below. Format matches the
    // canned-intents.json template in three-products/.
    var dynamicIntents = null;
    function setDynamicIntents(arr) {
      if (!Array.isArray(arr) || !arr.length) { dynamicIntents = null; return; }
      // Sort once by priority desc; first match wins.
      var sorted = arr.slice().sort(function (a, b) { return (b.priority || 0) - (a.priority || 0); });
      dynamicIntents = sorted;
    }
    function matchDynamic(text) {
      if (!dynamicIntents) return null;
      for (var i = 0; i < dynamicIntents.length; i++) {
        var intent = dynamicIntents[i];
        if (!intent || !Array.isArray(intent.match)) continue;
        if (!any(text, intent.match)) continue;
        if (Array.isArray(intent.matchAlsoOneOf) && intent.matchAlsoOneOf.length) {
          if (!any(text, intent.matchAlsoOneOf)) continue;
        }
        var body = String(intent.text || '');
        if (Array.isArray(intent.prefixIfAny)) {
          for (var p = 0; p < intent.prefixIfAny.length; p++) {
            var pre = intent.prefixIfAny[p];
            if (pre && Array.isArray(pre.match) && any(text, pre.match)) {
              body = String(pre.text || '') + body;
              break;
            }
          }
        }
        return {
          intent: intent.intent || 'canned',
          text: body,
          actions: Array.isArray(intent.actions) && intent.actions.length ? intent.actions.slice() : null,
          handoff: !!intent.handoff,
          handoffPrompt: intent.handoffPrompt || null,
        };
      }
      return null;
    }

    return {
      // Allow late-binding of server-fetched intents.
      setDynamic: setDynamicIntents,
      match: function (rawText) {
        var text = String(rawText || '').toLowerCase();
        // Server-fetched intents win when they're loaded.
        var dyn = matchDynamic(text);
        if (dyn) return dyn;
        // Contact intent takes precedence over the fee/membership
        // intents — if the visitor wants a human, give them the form
        // directly without confirmation rounds.
        if (isContactQ(text)) {
          return {
            intent: 'contact_james',
            text: CONTACT_TEXT,
            handoff: true,           // form opens directly; no buttons
            handoffPrompt: "Drop your name + phone or email and James' team will reach out:",
          };
        }
        // Direct path picks come BEFORE the generic start-question so
        // a visitor saying "I want to be a brand ambassador" gets the
        // BA link immediately rather than the clarifier.
        if (isWholesalePick(text)) {
          return { intent: 'enroll_wholesale', text: WHOLESALE_TEXT };
        }
        if (isRetailPick(text)) {
          return { intent: 'enroll_retail', text: RETAIL_TEXT };
        }
        if (isStartQ(text)) {
          return { intent: 'how_to_start', text: START_TEXT, actions: START_ACTIONS };
        }
        // Monthly variant takes precedence — both intents share fee
        // keywords, but "every month" is the more specific question.
        if (isMonthly(text) && (isFeeQ(text) || any(text, ['fee', 'membership', 'back office']))) {
          return {
            intent: 'smartship_related_membership',
            text: MONTHLY_TEXT,
            actions: STANDARD_ACTIONS,
          };
        }
        if (isFeeQ(text)) {
          var body = isHesitant(text) ? (HESITATION_PREFIX + FEE_TEXT) : FEE_TEXT;
          return {
            intent: 'back_office_fee_explanation',
            text: body,
            actions: STANDARD_ACTIONS,
          };
        }
        return null;
      },
    };
  })();

  // ─── State ───────────────────────────────────────────────────────
  var state = {
    open: false,
    initialized: false,
    visitorToken: null,
    owner: { name: 'Chat', avatar: null },
    messages: [],
    awaitingHuman: false,
    pollTimer: null,
    sending: false,
    formDraft: {},   // preserves user-typed form values across re-renders
    renderedSig: '', // last rendered messages signature to skip no-op renders
    qaMode: false,           // true when "Ask about a product" chip is active
    qaShownChip: false,      // chip is rendered on first open; flips after dismiss/use
    qaLastQuestion: '',      // last QA question (for "talk to James" handoff)
    qaLastAnswer: null,      // { text, citations, retrieved, refusal }
    qaSending: false,        // QA in-flight flag
    // Share-contact-info side panel (header user-plus button). Slides
    // over the chat body without unmounting messages — visitor can
    // cancel back to the conversation without losing context.
    infoOpen: false,
    infoSending: false,
    infoSubmitted: false,    // true after a successful submit this session
    infoError: null,         // last submit error string, null when ok
    infoStored: null,        // last loaded localStorage payload, used for pre-fill
  };

  // Refusal detection — server responds with this exact pattern when ungrounded.
  function isRefusal(text) {
    if (!text) return false;
    var t = String(text).toLowerCase();
    return t.indexOf("don't have that") !== -1 || t.indexOf('do not have that') !== -1 || t.indexOf('product info') !== -1;
  }

  function pushDataLayer(event, payload) {
    try {
      window.dataLayer = window.dataLayer || [];
      var entry = { event: event };
      if (payload) for (var k in payload) entry[k] = payload[k];
      window.dataLayer.push(entry);
    } catch (e) { /* */ }
  }

  function messagesSignature() {
    var n = state.messages.length;
    var last = n ? (state.messages[n - 1].id || state.messages[n - 1].text || '') : '';
    var qaSig = (state.qaShownChip ? '1' : '0') + (state.qaMode ? 'm' : '') + (state.qaLastAnswer ? ':' + (state.qaLastAnswer.text || '').slice(0, 24) : '');
    return n + '|' + last + '|' + (state.awaitingHuman ? '1' : '0') + '|' + qaSig;
  }

  // ─── DOM helpers ─────────────────────────────────────────────────
  function el(tag, attrs, children) {
    var node = document.createElement(tag);
    if (attrs) {
      for (var k in attrs) {
        // Skip null / undefined / false so callers can pass a tri-state
        // (e.g. `disabled: cond ? 'disabled' : null`) without dropping
        // the literal string "null" into the DOM. setAttribute('disabled',
        // null) writes disabled="null" which is truthy → button stays
        // disabled forever. Bug repro before this fix: handoff Send
        // button never activated.
        if (attrs[k] === null || attrs[k] === undefined || attrs[k] === false) continue;
        if (k === 'class') node.className = attrs[k];
        else if (k === 'style') node.style.cssText = attrs[k];
        else if (k.indexOf('on') === 0) node.addEventListener(k.slice(2).toLowerCase(), attrs[k]);
        else node.setAttribute(k, attrs[k]);
      }
    }
    if (children) {
      var arr = Array.isArray(children) ? children : [children];
      for (var i = 0; i < arr.length; i++) {
        var c = arr[i];
        if (c == null) continue;
        if (typeof c === 'string') node.appendChild(document.createTextNode(c));
        else node.appendChild(c);
      }
    }
    return node;
  }

  // ─── Style injection ─────────────────────────────────────────────
  function injectStyles() {
    var s = document.createElement('style');
    s.id = 'ant-widget-styles';
    s.textContent = (
      // The floating bottom-right bubble is replaced by the top CTA
      // (#antw-cta). Keep the rule defined so the DOM node hidden by
      // CSS doesn't flicker into view if existing pages still expect
      // a bubble; never displayed.
      '#antw-bubble{display:none !important;}' +

      // Top-anchored "Talk to <ownerFirstName>" CTA. Fixed at the top
      // of the viewport so it never scrolls off the page. Replaces the
      // floating bottom-right chat icon. Drop-down tab styling (sharp
      // top edge, rounded bottom corners) so it visually attaches to
      // the top of the viewport. env(safe-area-inset-top) keeps it
      // clear of the iOS notch in PWA / standalone mode.
      '#antw-cta{position:fixed;top:env(safe-area-inset-top);left:50%;transform:translateX(-50%);z-index:2147483645;background:' + CFG.accent + ';color:' + CFG.bg + ';padding:11px 24px;border:none;border-radius:0 0 14px 14px;font-weight:700;font-size:14px;letter-spacing:0.2px;cursor:pointer;box-shadow:0 6px 22px rgba(0,0,0,0.42);font-family:-apple-system,BlinkMacSystemFont,"Segoe UI",sans-serif;display:inline-flex;align-items:center;gap:8px;transition:filter 0.15s,transform 0.1s;max-width:90vw;white-space:nowrap;overflow:hidden;text-overflow:ellipsis;}' +
      '#antw-cta:hover{filter:brightness(1.06);}' +
      '#antw-cta:active{transform:translateX(-50%) translateY(1px);}' +
      '#antw-cta svg{width:14px;height:14px;flex-shrink:0;}' +
      // Hide the CTA while the chat panel is open so it doesn\'t sit
      // on top of the conversation. The toggleOpen() function
      // toggles body.antw-chat-open in lockstep with state.open.
      'body.antw-chat-open #antw-cta{display:none;}' +
      '#antw-bubble:hover{transform:translateY(-2px);box-shadow:0 12px 32px rgba(0,0,0,0.55);}' +
      '#antw-bubble svg{width:28px;height:28px;}' +
      '#antw-bubble .antw-badge{position:absolute;top:-2px;right:-2px;background:#dc2626;color:#fff;font-size:11px;font-weight:700;width:20px;height:20px;border-radius:50%;display:none;align-items:center;justify-content:center;border:2px solid ' + CFG.bg + ';font-family:-apple-system,BlinkMacSystemFont,"Segoe UI",sans-serif;}' +
      '#antw-panel{position:fixed;right:20px;bottom:90px;width:380px;height:560px;max-height:calc(100vh - 110px);background:' + CFG.bg + ';color:' + CFG.text + ';border:1px solid rgba(255,240,220,0.10);border-radius:18px;box-shadow:0 24px 60px rgba(0,0,0,0.6);display:none;flex-direction:column;overflow:hidden;z-index:2147483647;font-family:-apple-system,BlinkMacSystemFont,"Segoe UI",sans-serif;font-size:14px;line-height:1.5;}' +
      '#antw-panel.open{display:flex;}' +
      '#antw-header{display:flex;align-items:center;gap:12px;padding:14px 16px;border-bottom:1px solid rgba(255,240,220,0.10);background:linear-gradient(180deg,rgba(200,168,108,0.06),transparent);position:relative;}' +
      '#antw-header .antw-avatar{width:40px;height:40px;border-radius:50%;background:rgba(0,0,0,0.25);overflow:hidden;border:1px solid rgba(200,168,108,0.35);flex-shrink:0;}' +
      '#antw-header .antw-avatar img{width:100%;height:100%;object-fit:cover;}' +
      '#antw-header .antw-meta{flex:1;min-width:0;}' +
      '#antw-header .antw-name{font-size:15px;font-weight:700;letter-spacing:-0.2px;color:' + CFG.text + ';}' +
      '#antw-header .antw-status{font-size:12px;color:' + CFG.accent + ';font-weight:600;}' +
      '#antw-close{appearance:none;background:transparent;border:none;color:rgba(240,235,226,0.5);font-size:22px;cursor:pointer;padding:4px 8px;line-height:1;}' +
      '#antw-close:hover{color:' + CFG.text + ';}' +
      // Header "share contact info" pill. Sits between the bot name
      // and the close [×]. Pill with icon + label so visitors clearly
      // read it as a tappable CTA. After a successful submit, the
      // label flips to "Hi, <FirstName>" (or "Saved" if no name was
      // provided) and the pre/post icons swap — turning the CTA into
      // a personalized identity chip the visitor can tap to update.
      '#antw-share-info{appearance:none;background:rgba(200,168,108,0.12);border:1px solid rgba(200,168,108,0.45);color:' + CFG.accent + ';cursor:pointer;padding:5px 10px 5px 8px;border-radius:999px;display:inline-flex;align-items:center;gap:5px;flex-shrink:0;font-family:inherit;font-size:12px;font-weight:700;letter-spacing:0.2px;line-height:1;max-width:140px;white-space:nowrap;transition:background 0.15s,color 0.15s,border-color 0.15s,filter 0.15s;}' +
      '#antw-share-info:hover{background:' + CFG.accent + ';color:' + CFG.bg + ';border-color:' + CFG.accent + ';}' +
      '#antw-share-info svg{width:13px;height:13px;display:block;flex-shrink:0;}' +
      '#antw-share-info .antw-share-label{overflow:hidden;text-overflow:ellipsis;}' +
      '#antw-share-info .antw-share-icon-post{display:none;}' +
      '#antw-share-info.has-info{background:rgba(200,168,108,0.20);border-color:rgba(200,168,108,0.55);}' +
      '#antw-share-info.has-info:hover{filter:brightness(1.10);background:rgba(200,168,108,0.20);color:' + CFG.accent + ';border-color:' + CFG.accent + ';}' +
      '#antw-share-info.has-info .antw-share-icon-pre{display:none;}' +
      '#antw-share-info.has-info .antw-share-icon-post{display:block;}' +
      // Slide-in info form panel. Overlays the chat body so message
      // history stays mounted underneath — visitor can cancel back to
      // the conversation without losing context. position:absolute
      // inside the flex panel; #antw-panel needs position:relative
      // (added below) for this to anchor correctly.
      '#antw-info-panel{position:absolute;top:69px;left:0;right:0;bottom:0;background:' + CFG.bg + ';color:' + CFG.text + ';display:none;flex-direction:column;padding:18px 18px 16px;overflow-y:auto;z-index:5;animation:antw-info-slide 0.18s ease-out;}' +
      '#antw-info-panel.open{display:flex;}' +
      '@keyframes antw-info-slide{from{transform:translateY(8px);opacity:0;}to{transform:translateY(0);opacity:1;}}' +
      '.antw-info-title{font-size:16px;font-weight:700;letter-spacing:-0.2px;margin:0 0 4px;color:' + CFG.text + ';}' +
      '.antw-info-sub{font-size:13px;color:rgba(240,235,226,0.65);margin:0 0 14px;line-height:1.45;}' +
      '.antw-info-field{display:flex;flex-direction:column;gap:4px;margin-bottom:10px;}' +
      '.antw-info-label{font-size:11px;text-transform:uppercase;letter-spacing:1.2px;font-weight:700;color:rgba(240,235,226,0.55);}' +
      '.antw-info-input{width:100%;padding:10px 12px;background:rgba(255,240,220,0.04);border:1px solid rgba(240,235,226,0.18);color:' + CFG.text + ';border-radius:8px;font-size:16px;font-family:inherit;-webkit-appearance:none;appearance:none;outline:none;box-sizing:border-box;}' +
      '.antw-info-input:focus{border-color:' + CFG.accent + ';}' +
      '.antw-info-actions{display:flex;flex-direction:column;gap:8px;margin-top:8px;}' +
      '.antw-info-submit{padding:11px 16px;background:' + CFG.accent + ';color:' + CFG.bg + ';border:none;border-radius:8px;font-size:14px;font-weight:800;cursor:pointer;font-family:inherit;}' +
      '.antw-info-submit:disabled{opacity:0.55;cursor:wait;}' +
      '.antw-info-cancel{appearance:none;background:transparent;color:rgba(240,235,226,0.65);border:none;font-size:13px;font-family:inherit;cursor:pointer;padding:6px;text-decoration:underline;text-decoration-thickness:1px;text-underline-offset:2px;}' +
      '.antw-info-cancel:hover{color:' + CFG.text + ';}' +
      '.antw-info-prior{font-size:11px;color:rgba(240,235,226,0.5);margin:-2px 0 12px;}' +
      '.antw-info-error{font-size:12px;color:#E58B70;margin-top:4px;}' +
      '.antw-info-success{display:flex;flex-direction:column;align-items:flex-start;gap:14px;padding:8px 0;}' +
      '.antw-info-success-mark{width:46px;height:46px;border-radius:50%;background:' + CFG.accent + ';color:' + CFG.bg + ';display:flex;align-items:center;justify-content:center;font-size:24px;font-weight:800;}' +
      '.antw-info-success-text{font-size:14px;line-height:1.5;color:' + CFG.text + ';}' +
      '#antw-body{flex:1;overflow-y:auto;padding:16px;display:flex;flex-direction:column;gap:10px;}' +
      '.antw-msg{max-width:82%;padding:10px 14px;border-radius:14px;font-size:14px;line-height:1.45;word-wrap:break-word;white-space:pre-wrap;}' +
      '.antw-msg.bot,.antw-msg.owner{align-self:flex-start;background:rgba(255,240,220,0.06);border:1px solid rgba(255,240,220,0.10);color:' + CFG.text + ';border-top-left-radius:4px;}' +
      '.antw-msg.owner{background:rgba(200,168,108,0.10);border-color:rgba(200,168,108,0.30);}' +
      '.antw-msg.visitor{align-self:flex-end;background:' + CFG.accent + ';color:' + CFG.bg + ';font-weight:600;border-top-right-radius:4px;}' +
      '.antw-msg-meta{font-size:10px;text-transform:uppercase;letter-spacing:1.5px;font-weight:700;color:' + CFG.accent + ';margin-bottom:4px;opacity:0.8;}' +
      '.antw-msg.visitor .antw-msg-meta{color:rgba(15,14,13,0.6);}' +
      '.antw-options{display:flex;flex-direction:column;gap:6px;align-self:flex-start;max-width:90%;margin-top:-2px;}' +
      '.antw-option{appearance:none;background:rgba(200,168,108,0.10);color:' + CFG.text + ';border:1px solid rgba(200,168,108,0.35);border-radius:10px;padding:10px 14px;font-size:13px;font-weight:600;cursor:pointer;text-align:left;font-family:inherit;transition:all 0.15s;}' +
      '.antw-option:hover{background:' + CFG.accent + ';color:' + CFG.bg + ';border-color:' + CFG.accent + ';}' +
      '.antw-form{display:flex;flex-direction:column;gap:8px;align-self:flex-start;width:90%;background:rgba(255,240,220,0.04);border:1px solid rgba(200,168,108,0.20);border-radius:14px;padding:12px;margin-top:4px;}' +
      '.antw-form input{appearance:none;background:rgba(0,0,0,0.3);color:' + CFG.text + ';border:1px solid rgba(255,240,220,0.15);border-radius:8px;padding:10px 12px;font-size:14px;font-family:inherit;outline:none;}' +
      '.antw-form input:focus{border-color:' + CFG.accent + ';}' +
      '.antw-form button{appearance:none;background:' + CFG.accent + ';color:' + CFG.bg + ';border:none;border-radius:8px;padding:10px 14px;font-size:14px;font-weight:700;cursor:pointer;font-family:inherit;}' +
      '.antw-form button:hover{filter:brightness(1.08);}' +
      '#antw-footer{padding:12px 14px;border-top:1px solid rgba(255,240,220,0.10);display:flex;gap:8px;align-items:center;background:rgba(0,0,0,0.2);}' +
      // font-size MUST be at least 16px on mobile or iOS Safari
      // auto-zooms the page on focus — that's the "screen jumps and
      // Send is clipped" symptom users see. 16px disables the zoom.
      '#antw-input{flex:1;background:rgba(0,0,0,0.3);color:' + CFG.text + ';border:1px solid rgba(255,240,220,0.12);border-radius:10px;padding:10px 12px;font-size:16px;font-family:inherit;outline:none;}' +
      '#antw-input:focus{border-color:' + CFG.accent + ';}' +
      '#antw-send{appearance:none;background:' + CFG.accent + ';color:' + CFG.bg + ';border:none;border-radius:10px;padding:10px 14px;font-size:14px;font-weight:700;cursor:pointer;font-family:inherit;flex-shrink:0;}' +
      '#antw-send:disabled{opacity:0.5;cursor:not-allowed;}' +
      '#antw-typing{align-self:flex-start;font-size:12px;color:rgba(240,235,226,0.5);padding:6px 12px;font-style:italic;}' +
      // Typing dots inside an empty streaming bot bubble. Visible until
      // the first delta arrives, after which the bubble fills with text.
      '.antw-typing-dots{display:inline-flex;align-items:center;gap:5px;padding:2px 0;}' +
      '.antw-typing-dots span{width:7px;height:7px;border-radius:50%;background:' + CFG.accent + ';opacity:0.35;animation:antw-typing-pulse 1.2s ease-in-out infinite;}' +
      '.antw-typing-dots span:nth-child(2){animation-delay:0.18s;}' +
      '.antw-typing-dots span:nth-child(3){animation-delay:0.36s;}' +
      '@keyframes antw-typing-pulse{0%,60%,100%{opacity:0.35;transform:scale(1);}30%{opacity:1;transform:scale(1.25);}}' +
      // Soft caret on a streaming bubble to signal "still landing".
      '.antw-streaming::after{content:"";display:inline-block;width:7px;height:14px;background:' + CFG.accent + ';margin-left:3px;vertical-align:middle;animation:antw-caret-blink 0.85s steps(2) infinite;border-radius:1px;}' +
      '@keyframes antw-caret-blink{50%{opacity:0;}}' +
      // Inline auto-linkified URLs inside bot bubbles.
      '.antw-inline-link{color:' + CFG.accent + ';text-decoration:underline;text-decoration-thickness:1px;text-underline-offset:2px;word-break:break-all;}' +
      '.antw-inline-link:hover{text-decoration-thickness:2px;}' +
      '.antw-qa-chip{appearance:none;background:rgba(200,168,108,0.12);color:' + CFG.text + ';border:1px solid rgba(200,168,108,0.45);border-radius:999px;padding:8px 14px;font-size:13px;font-weight:600;cursor:pointer;text-align:left;font-family:inherit;align-self:flex-start;transition:all 0.15s;display:inline-flex;align-items:center;gap:6px;}' +
      '.antw-qa-chip:hover{background:' + CFG.accent + ';color:' + CFG.bg + ';border-color:' + CFG.accent + ';}' +
      '.antw-qa-chip.active{background:' + CFG.accent + ';color:' + CFG.bg + ';border-color:' + CFG.accent + ';}' +
      '.antw-citations{align-self:flex-start;max-width:90%;display:flex;flex-direction:column;gap:6px;margin-top:-2px;}' +
      '.antw-citations-label{font-size:10px;text-transform:uppercase;letter-spacing:1.5px;font-weight:700;color:rgba(240,235,226,0.55);}' +
      '.antw-citations-list{display:flex;flex-wrap:wrap;gap:6px;}' +
      '.antw-citation{display:inline-block;background:rgba(255,240,220,0.05);color:' + CFG.text + ';border:1px solid rgba(200,168,108,0.30);border-radius:999px;padding:5px 10px;font-size:12px;font-weight:600;text-decoration:none;transition:all 0.15s;}' +
      '.antw-citation:hover{background:rgba(200,168,108,0.18);border-color:' + CFG.accent + ';}' +
      '.antw-feedback{align-self:flex-start;display:flex;gap:8px;align-items:center;margin-top:-2px;font-size:12px;color:rgba(240,235,226,0.7);}' +
      '.antw-feedback-label{font-size:12px;color:rgba(240,235,226,0.6);}' +
      '.antw-feedback button{appearance:none;background:rgba(255,240,220,0.06);color:' + CFG.text + ';border:1px solid rgba(200,168,108,0.30);border-radius:8px;padding:6px 10px;font-size:12px;font-weight:600;cursor:pointer;font-family:inherit;}' +
      '.antw-feedback button:hover{background:' + CFG.accent + ';color:' + CFG.bg + ';border-color:' + CFG.accent + ';}' +
      '.antw-handoff{align-self:stretch;display:flex;flex-direction:column;gap:8px;margin:6px 0 4px;padding:12px 14px;background:rgba(200,168,108,0.06);border:1px solid rgba(200,168,108,0.30);border-radius:10px;}' +
      '.antw-handoff-label{font-size:12px;color:' + CFG.accent + ';font-weight:700;letter-spacing:0.3px;}' +
      '.antw-handoff-input{width:100%;padding:10px 12px;background:rgba(255,240,220,0.04);border:1px solid rgba(240,235,226,0.18);color:' + CFG.text + ';border-radius:8px;font-size:14px;font-family:inherit;-webkit-appearance:none;appearance:none;}' +
      '.antw-handoff-input:focus{outline:none;border-color:' + CFG.accent + ';}' +
      '.antw-handoff-textarea{min-height:84px;resize:vertical;line-height:1.45;}' +
      '.antw-handoff-send{padding:11px 16px;background:' + CFG.accent + ';color:' + CFG.bg + ';border:none;border-radius:8px;font-size:13px;font-weight:800;cursor:pointer;font-family:inherit;}' +
      '.antw-handoff-send:disabled{opacity:0.55;cursor:wait;}' +
      // Opt-in checkbox to share chat history with the team. Off by
      // default, on when the visitor actively wants the rep to have
      // context. No styling shouts — the UX should read as a quiet
      // courtesy, not a dark pattern.
      '.antw-handoff-history{display:flex;align-items:flex-start;gap:8px;font-size:12px;color:rgba(240,235,226,0.78);line-height:1.4;cursor:pointer;user-select:none;padding:2px 0;}' +
      '.antw-handoff-history-cb{width:16px;height:16px;margin-top:2px;accent-color:' + CFG.accent + ';flex-shrink:0;cursor:pointer;}' +
      '.antw-handoff-error{font-size:12px;color:#E58B70;}' +
      '.antw-handoff-done{font-size:13px;color:' + CFG.accent + ';font-weight:600;text-align:center;padding:6px 0;}' +
      // Mobile full-screen panel. dvh (dynamic viewport height) +
      // svh fallback ensure the panel resizes when the soft keyboard
      // pops up — without these the panel is a fixed 100vh tall, the
      // keyboard slides over the bottom (input + Send), and the
      // visitor can't see what they're typing. 100vh fallback for
      // older browsers that ignore unknown CSS units; 100dvh wins
      // on iOS 15.4+ / Android Chrome 108+.
      '@media (max-width:480px){' +
        '#antw-panel{right:0;bottom:0;width:100vw;height:100vh;height:100svh;height:100dvh;max-height:100vh;max-height:100dvh;border-radius:0;border:none;}' +
        // Mobile CTA: full width across the top, sharp corners so it
        // sits flush like a header bar. Slightly more padding for
        // tap-target comfort.
        '#antw-cta{left:0;right:0;transform:none;border-radius:0;padding:13px 16px;width:100%;justify-content:center;font-size:14px;}' +
        '#antw-cta:active{transform:translateY(1px);}' +
      '}' +
      // Full-window mode: panel IS the page. No bubble, no rounded
      // corners, no offset — fills the popup window edge to edge.
      (CFG.fullwindow ?
        '#antw-bubble{display:none !important;}' +
        // height + max-height redeclare for cascade-fallback support
        // — height:100% lands first (universal), then 100dvh wins
        // where supported so the keyboard shrinking doesn't push
        // the input + Send below the visible viewport.
        '#antw-panel{position:fixed !important;top:0 !important;left:0 !important;right:0 !important;bottom:0 !important;width:100% !important;height:100% !important;height:100dvh !important;max-height:100% !important;max-height:100dvh !important;border:none !important;border-radius:0 !important;box-shadow:none !important;}'
      : '')
    );
    document.head.appendChild(s);
  }

  // ─── Network ─────────────────────────────────────────────────────
  function api(path, method, body) {
    return fetch(CFG.api + path, {
      method: method || 'GET',
      headers: { 'Content-Type': 'application/json' },
      body: body ? JSON.stringify(body) : undefined,
      mode: 'cors',
      credentials: 'omit',
    }).then(function (r) {
      return r.json().then(function (j) { return { status: r.status, body: j }; });
    });
  }

  // ─── Token persistence ───────────────────────────────────────────
  //
  // Two storage layers for resilience:
  //   1. Cookie on the registrable domain (e.g. .example.com) — survives
  //      subdomain hops (example.com → app.example.com) and is more
  //      durable than localStorage in some privacy contexts.
  //   2. localStorage — survives cookie purges, no cross-subdomain sharing.
  // Both are written on save; read prefers cookie, falls back to localStorage.

  function getCookieDomain() {
    var host = (location.hostname || '').toLowerCase();
    if (!host || host === 'localhost' || /^\d+\.\d+\.\d+\.\d+$/.test(host)) return '';
    var parts = host.split('.');
    if (parts.length < 2) return '';
    // Drop only the leftmost label so we cover all subdomains.
    // For 2-part hostnames (foo.com) this returns "foo.com" — same effect.
    return '.' + parts.slice(-2).join('.');
  }
  var COOKIE_DOMAIN = SCRIPT.getAttribute('data-cookie-domain') || getCookieDomain();

  function setCookie(name, value, days) {
    if (!value) return;
    var maxAge = (days || 365) * 86400;
    var attrs = [
      name + '=' + encodeURIComponent(value),
      'Path=/',
      'Max-Age=' + maxAge,
      'SameSite=Lax',
    ];
    if (location.protocol === 'https:') attrs.push('Secure');
    if (COOKIE_DOMAIN) attrs.push('Domain=' + COOKIE_DOMAIN);
    try { document.cookie = attrs.join('; '); } catch (e) { /* */ }
  }
  function clearCookie(name) {
    var attrs = [name + '=; Path=/; Max-Age=0'];
    if (COOKIE_DOMAIN) attrs.push('Domain=' + COOKIE_DOMAIN);
    try { document.cookie = attrs.join('; '); } catch (e) { /* */ }
  }
  function getCookie(name) {
    try {
      var parts = (document.cookie || '').split(';');
      for (var i = 0; i < parts.length; i++) {
        var p = parts[i].trim();
        if (p.indexOf(name + '=') === 0) return decodeURIComponent(p.slice(name.length + 1));
      }
    } catch (e) { /* */ }
    return null;
  }

  function loadToken() {
    if (CFG.ephemeral) return null; // each page load = fresh session
    var fromCookie = getCookie(CFG.storageKey);
    if (fromCookie) return fromCookie;
    try { return localStorage.getItem(CFG.storageKey); } catch (e) { return null; }
  }
  function saveToken(t) {
    if (!t) return;
    if (CFG.ephemeral) return; // memory-only; do not persist
    setCookie(CFG.storageKey, t, 365);
    try { localStorage.setItem(CFG.storageKey, t); } catch (e) { /* */ }
  }
  function purgeToken() {
    clearCookie(CFG.storageKey);
    try { localStorage.removeItem(CFG.storageKey); } catch (e) { /* */ }
  }

  // Visitor-supplied contact info (name + email + phone). Optional —
  // the visitor opens the share-info panel manually. Stored once, used
  // to pre-fill the form on subsequent opens so updating is one-tap.
  // Never auto-submitted.
  function loadVisitorInfo() {
    try {
      var raw = localStorage.getItem(CFG.infoKey);
      if (!raw) return null;
      var v = JSON.parse(raw);
      // Defensive: v must be an object with at least one of the
      // contact channels. Garbage values are ignored, not thrown,
      // so a corrupt cell in storage doesn't break the panel.
      if (!v || typeof v !== 'object') return null;
      return v;
    } catch (e) { return null; }
  }
  function saveVisitorInfo(info) {
    if (!info || typeof info !== 'object') return;
    try { localStorage.setItem(CFG.infoKey, JSON.stringify(info)); } catch (e) { /* */ }
  }
  // Pill label after a successful submit. Prefers first name so the
  // chip reads as a personal greeting; falls back to "Saved" when
  // the visitor only shared email/phone.
  function shareLabelFor(info) {
    if (info && info.name && String(info.name).trim()) {
      var first = String(info.name).trim().split(/\s+/)[0];
      return 'Hi, ' + first;
    }
    if (info && (info.email || info.phone)) return 'Saved';
    return 'Contact me';
  }
  function applyShareInfoState(info) {
    if (!nodes.shareInfo || !nodes.shareLabel) return;
    var has = !!(info && (info.name || info.email || info.phone));
    nodes.shareLabel.textContent = shareLabelFor(info);
    if (has) {
      nodes.shareInfo.classList.add('has-info');
      nodes.shareInfo.setAttribute('aria-label', 'Update your contact info');
    } else {
      nodes.shareInfo.classList.remove('has-info');
      nodes.shareInfo.setAttribute('aria-label', 'Contact me');
    }
  }

  // ─── Rendering ───────────────────────────────────────────────────
  var nodes = {};

  function renderShell() {
    // Bubble kept as a hidden DOM node — `display:none` in CSS — so any
    // legacy code still referencing #antw-bubble (data-attribute hooks
    // on landing pages, automated tests, etc.) doesn\'t throw. The
    // visible entry point is now #antw-cta below.
    nodes.bubble = el('button', { id: 'antw-bubble', 'aria-label': 'Open chat' });
    nodes.bubble.innerHTML =
      '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round"><path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/></svg><span class="antw-badge" id="antw-badge"></span>';
    nodes.bubble.addEventListener('click', toggleOpen);
    document.body.appendChild(nodes.bubble);

    // Top-anchored "Talk to <name>" CTA — primary entry point. Always
    // visible at the top of the viewport (position:fixed) so visitors
    // can reach it without scrolling. Hidden via body.antw-chat-open
    // when the conversation panel is up. Skipped entirely in
    // fullwindow mode (the panel itself fills the viewport — there\'s
    // nothing to "open"). Skipped on the dedicated chat.html popup
    // for the same reason.
    if (!CFG.fullwindow) {
      nodes.cta = el('button', { id: 'antw-cta', type: 'button', 'aria-label': 'Open chat with ' + CFG.ownerFirstName });
      nodes.cta.innerHTML =
        '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.4" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">' +
          '<path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/>' +
        '</svg>' +
        '<span>Talk to ' + CFG.ownerFirstName + '</span>';
      nodes.cta.addEventListener('click', function () {
        // Click delegation so every existing chat entry-point still
        // works after the bubble was hidden. Priority:
        //   1. [data-chat-handoff="general"] element (JFV pattern) →
        //      click it, page's document-level handler routes through
        //      openHandoff('general') → antChatRunIntro with topical
        //      chip set ("Weight & Health" / "Service Dogs" etc).
        //   2. The bubble itself — programmatically click. Even though
        //      it's display:none its event listeners still fire. This
        //      preserves the THREE-International pattern where the
        //      landing's own wireBubble() binds the bubble click to
        //      antChatRunIntro({notice, actions: chips}). 50sp,
        //      getglpthree, Dee, Tania, Danielle Furlong all use
        //      this. Without delegation, the new top CTA would skip
        //      that listener and give visitors only the simple
        //      greeting — which is the regression we just hit.
        //   3. toggleOpen() — final fallback for any instance whose
        //      landing doesn't wire either path.
        var handoff = document.querySelector('[data-chat-handoff="general"]');
        if (handoff && typeof handoff.click === 'function') {
          handoff.click();
          return;
        }
        if (nodes.bubble && typeof nodes.bubble.click === 'function') {
          nodes.bubble.click();
          return;
        }
        toggleOpen();
      });
      document.body.appendChild(nodes.cta);
    }

    nodes.panel = el('div', { id: 'antw-panel', role: 'dialog', 'aria-label': 'Chat with us' });

    nodes.header = el('div', { id: 'antw-header' });
    nodes.avatar = el('div', { class: 'antw-avatar' });
    nodes.meta = el('div', { class: 'antw-meta' });
    nodes.headerName = el('div', { class: 'antw-name' }, CFG.title);
    nodes.headerStatus = el('div', { class: 'antw-status' }, 'Online');
    nodes.meta.appendChild(nodes.headerName);
    nodes.meta.appendChild(nodes.headerStatus);
    // "Share my contact info" header pill — sits between the bot
    // name and the close [×]. Icon + label so it reads as a clear
    // CTA, not a hidden affordance. After a successful submit the
    // label flips to "Hi, <FirstName>" (or "Saved") and the icon
    // swaps from user-plus to a checkmark; the pill stays tappable
    // so the visitor can update their info. Toggling it slides an
    // info-form panel over the chat body so message history stays
    // intact behind it.
    nodes.shareInfo = el('button', {
      id: 'antw-share-info',
      type: 'button',
      'aria-label': 'Contact me',
    });
    nodes.shareInfo.innerHTML =
      '<svg class="antw-share-icon-pre" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">' +
        '<path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2"/>' +
        '<circle cx="9" cy="7" r="4"/>' +
        '<line x1="19" y1="8" x2="19" y2="14"/>' +
        '<line x1="22" y1="11" x2="16" y2="11"/>' +
      '</svg>' +
      '<svg class="antw-share-icon-post" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.6" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">' +
        '<polyline points="20 6 9 17 4 12"/>' +
      '</svg>' +
      '<span class="antw-share-label">Contact me</span>';
    nodes.shareLabel = nodes.shareInfo.querySelector('.antw-share-label');
    nodes.shareInfo.addEventListener('click', toggleInfoPanel);

    var closeBtn = el('button', { id: 'antw-close', 'aria-label': 'Close chat' }, '×');
    closeBtn.addEventListener('click', function () {
      // In a dedicated popup, the close button should close the
      // window itself rather than collapse the panel into a bubble
      // that doesn't exist.
      if (CFG.fullwindow) {
        try { window.close(); return; } catch (e) { /* fall through */ }
      }
      toggleOpen();
    });
    nodes.header.appendChild(nodes.avatar);
    nodes.header.appendChild(nodes.meta);
    nodes.header.appendChild(nodes.shareInfo);
    nodes.header.appendChild(closeBtn);

    nodes.body = el('div', { id: 'antw-body' });

    nodes.footer = el('div', { id: 'antw-footer' });
    nodes.input = el('input', { id: 'antw-input', placeholder: 'Type a message…', 'aria-label': 'Message' });
    nodes.input.addEventListener('keydown', function (e) {
      if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); sendText(); }
    });
    nodes.send = el('button', { id: 'antw-send' }, 'Send');
    nodes.send.addEventListener('click', sendText);
    nodes.footer.appendChild(nodes.input);
    nodes.footer.appendChild(nodes.send);

    // Info-form side panel — built once, visibility toggled via the
    // .open class on the share-info button. Lives as a sibling of
    // body+footer so it overlays the chat without unmounting it.
    // Re-rendered on every open so prior submission state, draft
    // values, and error messages reflect the latest state.
    nodes.infoPanel = el('div', { id: 'antw-info-panel', role: 'region', 'aria-label': 'Share contact info' });

    nodes.panel.appendChild(nodes.header);
    nodes.panel.appendChild(nodes.body);
    nodes.panel.appendChild(nodes.infoPanel);
    nodes.panel.appendChild(nodes.footer);
    document.body.appendChild(nodes.panel);
  }

  function applyOwner(owner) {
    if (!owner) return;
    state.owner = owner;
    if (owner.name) nodes.headerName.textContent = owner.name;
    nodes.avatar.innerHTML = '';
    if (owner.avatar) {
      var img = el('img', { src: owner.avatar, alt: owner.name || '' });
      nodes.avatar.appendChild(img);
    } else {
      nodes.avatar.style.background = CFG.accent;
      nodes.avatar.style.color = CFG.bg;
      nodes.avatar.style.display = 'flex';
      nodes.avatar.style.alignItems = 'center';
      nodes.avatar.style.justifyContent = 'center';
      nodes.avatar.style.fontWeight = '700';
      nodes.avatar.textContent = (owner.name || '?').slice(0, 1).toUpperCase();
    }
  }

  function renderMessages() {
    // Capture scroll position BEFORE we tear the DOM. innerHTML=''
    // wipes scrollTop entirely (the body becomes empty), so we have
    // to restore it ourselves. Two cases:
    //
    //   1) Visitor was pinned to the bottom (within STICK_THRESHOLD)
    //      → after re-render, scroll to the new bottom (follow the
    //      stream).
    //   2) Visitor had scrolled up to read earlier text → after
    //      re-render, restore the previous scrollTop so they stay
    //      where they were. Approximate, but the streaming bot
    //      typically only grows the LAST message taller, so the
    //      visitor's reading position above doesn't shift much.
    var STICK_THRESHOLD = 60;
    var prevScrollTop = nodes.body ? nodes.body.scrollTop : 0;
    var wasAtBottom = !nodes.body || (
      nodes.body.scrollHeight - nodes.body.scrollTop - nodes.body.clientHeight < STICK_THRESHOLD
    );
    nodes.body.innerHTML = '';
    state.messages.forEach(function (m, idx) {
      var bubbleClasses = 'antw-msg ' + (m.role || 'bot');
      // Mid-stream bot turns get a blinking caret to signal "still
      // landing". Once streaming flips off the caret class drops.
      if (m.role === 'bot' && m.streaming && m.text) bubbleClasses += ' antw-streaming';
      var bubble = el('div', { class: bubbleClasses });
      // Show meta label only on bot/owner messages. The bot is named
      // per-tenant (e.g. "<Brand> Team") via the runtimeConfig pulled
      // from /canned-intents/<flow>; falls back to "the team" when
      // no per-tenant config is loaded yet.
      if (m.role === 'bot' || m.role === 'owner') {
        var who = m.role === 'owner' ? (state.owner.name || 'James') : runtimeConfig.botLabel;
        bubble.appendChild(el('div', { class: 'antw-msg-meta' }, who));
      }
      // Empty streaming bot bubble → animated typing dots until the
      // first delta lands. Otherwise plain text — but bot text gets
      // its URLs auto-linkified so visitors can click "https://…"
      // sequences (e.g. enrollment links) without us having to render
      // a separate action button.
      if (m.role === 'bot' && m.streaming && !m.text) {
        var dots = el('div', { class: 'antw-typing-dots' });
        dots.appendChild(el('span', {}));
        dots.appendChild(el('span', {}));
        dots.appendChild(el('span', {}));
        bubble.appendChild(dots);
      } else if (m.role === 'bot' && m.text) {
        var urlRe = /https?:\/\/[^\s<>"']+[^\s<>"',.;:!?)\]]/g;
        var lastIdx = 0;
        var match;
        while ((match = urlRe.exec(m.text)) !== null) {
          if (match.index > lastIdx) {
            bubble.appendChild(document.createTextNode(m.text.slice(lastIdx, match.index)));
          }
          var a = el('a', { href: match[0], target: '_blank', rel: 'noopener noreferrer', class: 'antw-inline-link' }, match[0]);
          bubble.appendChild(a);
          lastIdx = match.index + match[0].length;
        }
        if (lastIdx < m.text.length) {
          bubble.appendChild(document.createTextNode(m.text.slice(lastIdx)));
        }
      } else {
        bubble.appendChild(document.createTextNode(m.text || ''));
      }
      nodes.body.appendChild(bubble);

      // Citations attached directly to a bot turn (conversational
      // ephemeral mode). Each turn keeps its own sources so threads
      // don't lose attribution as the conversation grows.
      if (m.role === 'bot' && Array.isArray(m.citations) && m.citations.length) {
        var citWrap = el('div', { class: 'antw-citations' });
        citWrap.appendChild(el('div', { class: 'antw-citations-label' }, 'Sources:'));
        var list = el('div', { class: 'antw-citations-list' });
        m.citations.slice(0, 5).forEach(function (c) {
          if (!c || !c.url) return;
          var label = (c.label || c.url) + ' ↗';
          var a = el('a', { class: 'antw-citation', href: c.url, target: '_blank', rel: 'noopener' }, label);
          a.addEventListener('click', function () { pushDataLayer('qa_citation_clicked', { target: c.url }); });
          list.appendChild(a);
        });
        citWrap.appendChild(list);
        nodes.body.appendChild(citWrap);
      }

      // Handoff inline form — shown when the bot signaled this turn
      // needs a human follow-up. Captures name + phone-or-email and
      // creates a lead via /public-form. Three states: form (default),
      // sending, done.
      if (m.role === 'bot' && m.handoff) {
        var handoffWrap = el('div', { class: 'antw-handoff' });
        if (m.handoffDone) {
          handoffWrap.appendChild(el('div', { class: 'antw-handoff-done' }, 'Got it. The team will reach out.'));
        } else {
          var promptText = m.handoffPrompt || 'Drop your contact and the team will reach out:';
          var label = el('div', { class: 'antw-handoff-label' }, promptText);
          handoffWrap.appendChild(label);
          // Three-field intake: Name + Phone + Email. Validation requires
          // a name plus at least one of phone or email — most visitors
          // don't have both handy, asking for both as required would
          // bounce them.
          var nameInp  = el('input', { class: 'antw-handoff-input', type: 'text',  placeholder: 'Your name',     autocomplete: 'name' });
          var phoneInp = el('input', { class: 'antw-handoff-input', type: 'tel',   placeholder: 'Phone number',  autocomplete: 'tel',   inputmode: 'tel' });
          var emailInp = el('input', { class: 'antw-handoff-input', type: 'email', placeholder: 'Email address', autocomplete: 'email', inputmode: 'email' });
          // Optional free-text message field. Shown when the host
          // page invoked antChatHandoff with messageRequired:true —
          // used by topical "Open chat with X" buttons that want to
          // capture a real message alongside the contact info before
          // routing to the back-end Message system (no LLM in the loop).
          var msgInp = null;
          if (m.handoffMessageRequired) {
            msgInp = el('textarea', {
              class: 'antw-handoff-input antw-handoff-textarea',
              placeholder: m.handoffMessagePlaceholder || 'What do you want to share?',
              rows: '3',
            });
          }
          if (state.formDraft) {
            if (state.formDraft._handoffName)  nameInp.value  = state.formDraft._handoffName;
            if (state.formDraft._handoffPhone) phoneInp.value = state.formDraft._handoffPhone;
            if (state.formDraft._handoffEmail) emailInp.value = state.formDraft._handoffEmail;
            if (msgInp && state.formDraft._handoffMessage) msgInp.value = state.formDraft._handoffMessage;
          }
          nameInp.addEventListener('input',  function () { state.formDraft._handoffName  = nameInp.value;  });
          phoneInp.addEventListener('input', function () { state.formDraft._handoffPhone = phoneInp.value; });
          emailInp.addEventListener('input', function () { state.formDraft._handoffEmail = emailInp.value; });
          if (msgInp) {
            msgInp.addEventListener('input', function () { state.formDraft._handoffMessage = msgInp.value; });
          }
          handoffWrap.appendChild(nameInp);
          handoffWrap.appendChild(phoneInp);
          handoffWrap.appendChild(emailInp);
          if (msgInp) handoffWrap.appendChild(msgInp);
          // Opt-in chat-history capture. Default UNCHECKED — visitor's
          // questions stay private unless they actively choose to send
          // them along with their contact info. Without this checkbox
          // ticked, only name + contact reaches the team.
          var historyRow = el('label', { class: 'antw-handoff-history' });
          var historyInp = el('input', { class: 'antw-handoff-history-cb', type: 'checkbox' });
          historyInp.checked = !!(state.formDraft && state.formDraft._handoffShareHistory);
          historyInp.addEventListener('change', function () {
            state.formDraft._handoffShareHistory = !!historyInp.checked;
          });
          historyRow.appendChild(historyInp);
          historyRow.appendChild(el('span', {}, 'Share this chat so the team can pick up where we left off'));
          handoffWrap.appendChild(historyRow);
          if (m.handoffError) {
            handoffWrap.appendChild(el('div', { class: 'antw-handoff-error' }, m.handoffError));
          }
          var sendBtn = el('button', {
            class: 'antw-handoff-send',
            type: 'button',
            disabled: m.handoffSending ? 'disabled' : null,
          }, m.handoffSending ? 'Sending…' : 'Send');
          var capturedIdx = idx; // freeze for closure
          sendBtn.addEventListener('click', function () {
            var nm    = (nameInp.value  || '').trim();
            var phone = (phoneInp.value || '').trim();
            var email = (emailInp.value || '').trim();
            var msg   = msgInp ? (msgInp.value || '').trim() : '';
            if (!nm)    { nameInp.focus();  return; }
            var phoneOk = phone && phone.replace(/\D/g, '').length >= 7;
            var emailOk = email && /^[\w.+-]+@[\w-]+\.[\w.-]+$/.test(email);
            if (!phoneOk && !emailOk) { (phone ? phoneInp : emailInp).focus(); return; }
            if (m.handoffMessageRequired && !msg) { msgInp && msgInp.focus(); return; }
            // Prefer phone for the contact channel; pass email along too.
            var contact = phoneOk ? phone : email;
            submitHandoff(nm, contact, capturedIdx, {
              phone: phoneOk ? phone : '',
              email: emailOk ? email : '',
              shareHistory: !!historyInp.checked,
              topic: m.handoffTopic || null,
              message: msg || null,
            });
          });
          handoffWrap.appendChild(sendBtn);
        }
        nodes.body.appendChild(handoffWrap);
      }

      // Render options under the LAST bot message only (if not awaiting human)
      var isLast = idx === state.messages.length - 1;
      if (isLast && m.role === 'bot' && !state.awaitingHuman) {
        if (m.options && m.options.length) {
          var optWrap = el('div', { class: 'antw-options' });
          m.options.forEach(function (opt) {
            var btn = el('button', { class: 'antw-option' }, opt.label);
            btn.addEventListener('click', function () {
              if (state.sending) return;
              sendAction({ type: 'pick', value: opt.value });
            });
            optWrap.appendChild(btn);
          });
          nodes.body.appendChild(optWrap);
        }
        // Canned-intent action buttons (back-office fee, etc).
        // Hidden once the user clicks one, or once the handoff form
        // is open (m.handoff && !m.handoffDone). 'dismiss' just clears
        // the buttons; 'handoff' opens the inline contact form.
        if (m.actions && m.actions.length && !m.handoff) {
          var actWrap = el('div', { class: 'antw-options' });
          var captured = m;
          m.actions.forEach(function (act) {
            var btn;
            if (act.kind === 'link' && act.url) {
              // External-link action — render as an anchor styled like
              // a button so a click opens the URL in a new tab. No
              // state mutation; the buttons stay so the visitor can
              // pick the other one if they change their mind.
              //
              // Optional `followup`: when set, after the link opens
              // we dispatch the followup string back into the chat as
              // if the visitor typed it — kicks off a step-by-step
              // helper conversation beside the new tab. Used by
              // enrollment CTAs ("Open the enrollment" + start the
              // sign-up walkthrough).
              btn = el('a', { class: 'antw-option', href: act.url, target: '_blank', rel: 'noopener noreferrer' }, act.label);
              btn.addEventListener('click', function () {
                pushDataLayer('qa_canned_link_clicked', { intent: captured.cannedIntent, url: act.url });
                if (typeof act.followup === 'string' && act.followup.trim()) {
                  // Clear the action chips so they don't re-fire and
                  // start the helper conversation. Don't preventDefault
                  // — let the link still open in the new tab.
                  captured.actions = null;
                  renderMessages();
                  if (nodes.input) {
                    nodes.input.value = act.followup;
                    setTimeout(function () { sendText(); }, 0);
                  }
                }
              });
            } else {
              btn = el('button', { class: 'antw-option' }, act.label);
              btn.addEventListener('click', function () {
                if (act.kind === 'handoff') {
                  // Per-action overrides let one bot bubble offer multiple
                  // topical handoff entry points (e.g. "Pick your focus:
                  // Service Dogs / Build / Vet-to-vet"), each with its
                  // own pre-tailored greeting + prompt + placeholder +
                  // topic slug. Falls back to whatever was already on
                  // the message if a button doesn't override.
                  if (typeof act.greeting === 'string' && act.greeting.trim()) {
                    captured.text = act.greeting.trim();
                  }
                  if (act.prompt !== undefined) {
                    captured.handoffPrompt = (typeof act.prompt === 'string' && act.prompt.trim()) || null;
                  }
                  if (act.placeholder !== undefined) {
                    captured.handoffMessagePlaceholder = (typeof act.placeholder === 'string' && act.placeholder) || null;
                  }
                  if (act.topic !== undefined) {
                    captured.handoffTopic = (typeof act.topic === 'string' && act.topic) || null;
                  }
                  if (act.messageRequired !== undefined) {
                    captured.handoffMessageRequired = !!act.messageRequired;
                  }
                  captured.handoff = true;
                  captured.actions = null;
                  pushDataLayer('qa_canned_handoff_clicked', { intent: captured.cannedIntent, topic: captured.handoffTopic });
                  renderMessages();
                } else if (act.kind === 'open-topical') {
                  // Replace the current bubble with a fresh topical
                  // bot bubble — bot greets the visitor in the chosen
                  // topic, with optional sub-actions (sign-up, talk-
                  // to-James, etc.). The visitor can then ask
                  // questions normally and the AI Q&A pipeline handles
                  // them. Used by the hero "Pick your focus" picker.
                  state.messages = [{
                    role: 'bot',
                    text: (typeof act.opener === 'string' && act.opener.trim()) || act.label,
                    actions: Array.isArray(act.actions) ? act.actions : null,
                    cannedIntent: 'page_anchor_topical_' + (act.topic || 'unknown'),
                  }];
                  pushDataLayer('qa_canned_topical_clicked', { topic: act.topic, label: act.label });
                  renderMessages();
                  setTimeout(function () { nodes.input && nodes.input.focus(); }, 50);
                } else if (act.kind === 'send' && act.value) {
                  // Submit a follow-up question on the visitor's
                  // behalf. Stuff the input + call sendText so the
                  // turn flows through the same canned-intent /
                  // LLM pipeline as if they typed it.
                  captured.actions = null;
                  pushDataLayer('qa_canned_send_clicked', { intent: captured.cannedIntent, value: act.value });
                  renderMessages();
                  if (nodes.input) {
                    nodes.input.value = act.value;
                    setTimeout(function () { sendText(); }, 0);
                  }
                } else {
                  // 'dismiss' (Keep going) or any unknown kind — just
                  // clear the buttons. The user keeps typing.
                  captured.actions = null;
                  pushDataLayer('qa_canned_dismiss_clicked', { intent: captured.cannedIntent });
                  renderMessages();
                  setTimeout(function () { nodes.input && nodes.input.focus(); }, 50);
                }
              });
            }
            actWrap.appendChild(btn);
          });
          nodes.body.appendChild(actWrap);
        }
        if (m.fields && m.fields.length) {
          var form = el('form', { class: 'antw-form' });
          var inputs = {};
          m.fields.forEach(function (f) {
            var inp = el('input', { type: f.type || 'text', placeholder: f.label, name: f.name, autocomplete: f.type === 'email' ? 'email' : (f.type === 'tel' ? 'tel' : 'name') });
            // Restore any draft value the user already typed before a re-render
            if (state.formDraft && typeof state.formDraft[f.name] === 'string') {
              inp.value = state.formDraft[f.name];
            }
            inp.addEventListener('input', function () {
              state.formDraft[f.name] = inp.value;
            });
            inputs[f.name] = inp;
            form.appendChild(inp);
          });
          var sub = el('button', { type: 'submit' }, 'Send');
          form.appendChild(sub);
          form.addEventListener('submit', function (e) {
            e.preventDefault();
            if (state.sending) return;
            var val = {};
            for (var k in inputs) val[k] = inputs[k].value.trim();
            if (!val.email) { inputs.email && inputs.email.focus(); return; }
            state.formDraft = {}; // clear draft on submit
            sendAction({ type: 'submit', value: val });
          });
          nodes.body.appendChild(form);
        }
      }

      // Optional second bubble for compound bot messages — used by
      // antChatRunIntro's combo-greeting flow where the bot wants to
      // sandwich action chips between two text segments. Renders
      // unconditionally on any bot message that has m.text2 set,
      // not gated by isLast (so the trailing line stays visible after
      // the visitor types).
      if (m.role === 'bot' && (m.text2 || m.streaming2)) {
        var bubble2Classes = 'antw-msg bot';
        if (m.streaming2 && m.text2) bubble2Classes += ' antw-streaming';
        var bubble2 = el('div', { class: bubble2Classes });
        bubble2.appendChild(el('div', { class: 'antw-msg-meta' }, runtimeConfig.botLabel));
        if (m.streaming2 && !m.text2) {
          var dots2 = el('div', { class: 'antw-typing-dots' });
          dots2.appendChild(el('span', {}));
          dots2.appendChild(el('span', {}));
          dots2.appendChild(el('span', {}));
          bubble2.appendChild(dots2);
        } else {
          bubble2.appendChild(document.createTextNode(m.text2 || ''));
        }
        nodes.body.appendChild(bubble2);
      }
    });

    // QA chip + legacy answer block only in non-ephemeral mode. In
    // ephemeral (guide popup) mode, the entire thread is the LLM
    // conversation — chips and the legacy single-shot answer surface
    // would clutter it.
    if (!CFG.ephemeral && !state.qaLastAnswer) {
      var chip = el('button', { class: 'antw-qa-chip' + (state.qaMode ? ' active' : ''), type: 'button', 'aria-label': 'Ask about a product' }, 'Ask about a product');
      chip.addEventListener('click', function () { enterQAMode(); });
      nodes.body.appendChild(chip);
    }

    // QA answer bubble + citations + feedback affordance — also gated
    // off in ephemeral mode (per-message citations + multi-turn flow
    // already handle this).
    if (!CFG.ephemeral && state.qaLastAnswer) {
      var ans = state.qaLastAnswer;
      var ansBubble = el('div', { class: 'antw-msg bot' });
      ansBubble.appendChild(el('div', { class: 'antw-msg-meta' }, runtimeConfig.botLabel));
      ansBubble.appendChild(document.createTextNode(ans.text || ''));
      nodes.body.appendChild(ansBubble);

      if (ans.citations && ans.citations.length) {
        var capped = ans.citations.slice(0, 5);
        var citWrap = el('div', { class: 'antw-citations' });
        citWrap.appendChild(el('div', { class: 'antw-citations-label' }, 'Sources:'));
        var list = el('div', { class: 'antw-citations-list' });
        capped.forEach(function (c) {
          if (!c || !c.url) return;
          var label = (c.label || c.url) + ' ↗';
          var a = el('a', { class: 'antw-citation', href: c.url, target: '_blank', rel: 'noopener' }, label);
          a.addEventListener('click', function () {
            pushDataLayer('qa_citation_clicked', { target: c.url });
          });
          list.appendChild(a);
        });
        citWrap.appendChild(list);
        nodes.body.appendChild(citWrap);
      }

      var fb = el('div', { class: 'antw-feedback' });
      var fbLabel = ans.refusal ? 'Want to talk to James?' : 'Was that helpful?';
      fb.appendChild(el('span', { class: 'antw-feedback-label' }, fbLabel));
      if (!ans.refusal) {
        var yesBtn = el('button', { type: 'button' }, 'Yes');
        yesBtn.addEventListener('click', function () {
          state.qaLastAnswer = null;
          state.qaShownChip = false;
          renderMessagesIfChanged();
        });
        fb.appendChild(yesBtn);
      }
      var noBtn = el('button', { type: 'button' }, ans.refusal ? 'Talk to James' : 'No, talk to James');
      noBtn.addEventListener('click', function () {
        handoffToLeadChat();
      });
      fb.appendChild(noBtn);
      nodes.body.appendChild(fb);
    }

    // Restore scroll position. wasAtBottom → follow the stream;
    // otherwise → put them back where they were so they can keep
    // reading. innerHTML='' wiped scrollTop to 0, so this restoration
    // is required even for the at-bottom case.
    if (wasAtBottom) {
      nodes.body.scrollTop = nodes.body.scrollHeight;
    } else {
      nodes.body.scrollTop = prevScrollTop;
    }
    state.renderedSig = messagesSignature();
  }

  function renderMessagesIfChanged() {
    if (messagesSignature() !== state.renderedSig) renderMessages();
  }

  /**
   * Type a full string into a bot message gradually, mirroring the
   * SSE streaming UX. Used for paths where the server already sent
   * the full text in one shot (canned intents, off-topic JSON
   * short-circuit). Without this, only LLM-streamed answers animate
   * — which makes the chat feel inconsistent on tenants whose KB
   * is small (more questions hit the canned/short-circuit paths).
   *
   * msg.streaming flips to true during the type-out so the body's
   * blinking caret affordance applies, then false on completion.
   */
  function typeOutBotMessage(msg, fullText, opts) {
    opts = opts || {};
    var chunkSize = opts.chunkSize || 4;
    var stepMs    = opts.stepMs    || 24;
    var onDone    = opts.onDone    || function () {};
    msg.text = '';
    msg.streaming = true;
    renderMessages();
    if (!fullText) {
      msg.streaming = false;
      renderMessages();
      onDone();
      return;
    }
    var i = 0;
    var timer = setInterval(function () {
      i = Math.min(i + chunkSize, fullText.length);
      msg.text = fullText.slice(0, i);
      renderMessages();
      if (i >= fullText.length) {
        clearInterval(timer);
        msg.streaming = false;
        renderMessages();
        onDone();
      }
    }, stepMs);
  }

  // ─── Actions ─────────────────────────────────────────────────────
  function applyResponse(resp) {
    if (resp.body && resp.body.owner) applyOwner(resp.body.owner);
    if (resp.body && resp.body.visitorToken) {
      state.visitorToken = resp.body.visitorToken;
      saveToken(resp.body.visitorToken);
    }
    if (resp.body && resp.body.conversation) {
      state.messages = resp.body.conversation.messages || [];
      state.awaitingHuman = !!resp.body.conversation.awaitingHuman;
    }
    renderMessagesIfChanged();
  }

  function startChat() {
    return api('/lead-chat/start', 'POST', { tenant: CFG.tenant, inv: CFG.inv }).then(function (resp) {
      if (resp.status >= 400) {
        console.warn('[ant-widget] start failed:', resp.body);
        return;
      }
      applyResponse(resp);
    }).catch(function (e) {
      console.warn('[ant-widget] start network error:', e);
    });
  }

  function pollConversation() {
    if (!state.visitorToken) return Promise.resolve();
    return api('/lead-chat/conversation/' + encodeURIComponent(state.visitorToken)).then(function (resp) {
      if (resp.status === 404) {
        // Token is stale (chat purged server-side or wrong instance). Reset.
        purgeToken();
        state.visitorToken = null;
        state.messages = [];
        state.awaitingHuman = false;
        return;
      }
      if (resp.status >= 400) return;
      var prevLen = state.messages.length;
      applyResponse(resp);
      if (!state.open && state.messages.length > prevLen) {
        var badge = document.getElementById('antw-badge');
        if (badge) {
          badge.textContent = String(state.messages.length - prevLen);
          badge.style.display = 'flex';
        }
      }
    }).catch(function () { /* */ });
  }

  function sendText() {
    var text = nodes.input.value.trim();
    if (!text || state.sending || state.qaSending) return;
    nodes.input.value = '';
    // Compliance-pinned canned intents (back-office fee, contact,
    // etc) bypass both the LLM and the lead-chat — same rendering in
    // either mode. If the canned response carries `handoff: true`,
    // the inline name+phone+email form opens directly under the
    // bubble (no "tap a button" round-trip).
    var canned = CANNED_INTENTS.match(text);
    if (canned) {
      state.messages.push({ role: 'visitor', text: text });
      // Push the bot bubble empty + streaming so the blinking caret
      // shows during type-out, matching the SSE streaming UX.
      // Actions and handoff form attach AFTER the text finishes
      // landing, so the visitor reads the message before the form
      // pops in.
      var cannedMsg = {
        role: 'bot',
        text: '',
        streaming: true,
        cannedIntent: canned.intent,
      };
      state.messages.push(cannedMsg);
      pushDataLayer('qa_canned_intent', { intent: canned.intent, flow: CFG.flow });
      typeOutBotMessage(cannedMsg, canned.text, {
        onDone: function () {
          if (canned.handoff) {
            cannedMsg.handoff = true;
            cannedMsg.handoffPrompt = canned.handoffPrompt || null;
          } else if (canned.actions) {
            cannedMsg.actions = canned.actions;
          }
          renderMessages();
          setTimeout(function () { nodes.input && nodes.input.focus(); }, 50);
        },
      });
      return;
    }
    if (CFG.ephemeral) {
      submitConvo(text);
      return;
    }
    // Non-ephemeral (main landing): keep the existing single-shot
    // Q&A path with the "Ask about a product" chip affordance +
    // lead-chat fallback.
    submitQA(text);
  }

  // Read step context from the surrounding guide popup. Set by
  // guide.html on each step transition as window.__guideContext.
  // Returns null when not in a guide context.
  function getGuideContext() {
    try {
      if (typeof window === 'undefined') return null;
      var c = window.__guideContext;
      if (!c || typeof c !== 'object') return null;
      return {
        stepNum: typeof c.stepNum === 'number' ? c.stepNum : undefined,
        stepKey: c.stepKey || undefined,
        stepTitle: c.stepTitle || undefined,
        stepWhatYoureDoing: c.stepWhatYoureDoing || undefined,
        stepWhyItMatters: c.stepWhyItMatters || undefined,
      };
    } catch (e) { return null; }
  }

  /**
   * Conversational multi-turn POST to /qa/<flow>/ask. Pushes the
   * visitor's message to state.messages, sends prior turns + latest
   * question + step context, appends the assistant's reply on
   * response. Citations attach to the bot message so renderMessages
   * draws them under that bubble.
   */
  function submitConvo(text) {
    if (!text || state.sending) return;
    state.sending = true;
    nodes.send.disabled = true;

    state.messages.push({ role: 'visitor', text: text });

    // History = everything BEFORE this turn. The empty bot placeholder
    // we're about to push gets filtered by .text length.
    var history = state.messages
      .filter(function (m) { return (m.role === 'visitor' || m.role === 'bot') && m.text; })
      .slice(-13, -1) // exclude the just-pushed visitor; server appends it
      .map(function (m) {
        return { role: m.role === 'bot' ? 'assistant' : 'user', content: m.text || '' };
      });

    // Streaming placeholder bubble. Empty text + streaming=true triggers
    // the typing-dots affordance until the first delta arrives.
    var botMsg = { role: 'bot', text: '', streaming: true };
    state.messages.push(botMsg);
    renderMessages();

    var ctx = getGuideContext();
    pushDataLayer('qa_question_asked', { question: text, flow: CFG.flow, step: ctx && ctx.stepKey });

    var streamReady = !!(window.fetch && window.ReadableStream && typeof TextDecoder !== 'undefined');
    var url = CFG.api + '/qa/' + encodeURIComponent(CFG.flow) + '/ask';
    var body = JSON.stringify({
      question: text,
      messages: history,
      context: ctx,
      stream: streamReady,
    });
    // Paced display: SSE deltas land as fast as Anthropic streams them
    // (network-bound), but we drip them onto the screen at a comfortable
    // reading rate so the visitor can scan along instead of seeing a
    // wall of text drop in instantly. ~70 chars/sec — fast enough that
    // a paragraph lands in a couple seconds, slow enough to feel live.
    // (Greeting animations use their own slower per-call rate via
    // antChatRunIntro({chunkSize, stepMs}); these knobs only govern
    // the AI Q&A reply stream.)
    var STREAM_CHARS_PER_TICK = 4;
    var STREAM_TICK_MS        = 28;
    var pendingText   = '';
    var displayedText = '';
    var paceTimer    = null;
    var pendingDone  = null;        // set when SSE done arrives mid-pace
    function pumpDisplay() {
      if (paceTimer) return;
      paceTimer = setInterval(function () {
        if (displayedText.length >= pendingText.length) {
          clearInterval(paceTimer);
          paceTimer = null;
          if (pendingDone) {
            var d = pendingDone;
            pendingDone = null;
            finalizeBot(d);
          }
          return;
        }
        var nextLen = Math.min(displayedText.length + STREAM_CHARS_PER_TICK, pendingText.length);
        displayedText = pendingText.slice(0, nextLen);
        botMsg.text = displayedText;
        renderMessages();
      }, STREAM_TICK_MS);
    }
    function finalizeBot(b) {
      botMsg.streaming = false;
      if (typeof b.text === 'string' && b.text.length) botMsg.text = b.text;
      botMsg.citations = Array.isArray(b.citations) ? b.citations.slice(0, 5) : [];
      botMsg.handoff = !!b.handoff;
      // Inline action buttons emitted by the LLM (text-options → chips).
      // Reuses the same rendering path canned-intents use for actions —
      // 'send' kind dispatches act.value as a fake visitor turn.
      if (Array.isArray(b.actions) && b.actions.length) {
        botMsg.actions = b.actions.slice(0, 5);
      }
      pushDataLayer('qa_answer_received', {
        retrieved: b.retrieved || 0,
        citations: (botMsg.citations || []).length,
        handoff: botMsg.handoff,
        actions: (botMsg.actions || []).length,
      });
      renderMessages();
    }
    function failBot(msg) {
      if (paceTimer) { clearInterval(paceTimer); paceTimer = null; }
      botMsg.streaming = false;
      if (!botMsg.text) botMsg.text = msg || "Sorry — I couldn't reach the assistant. Try again in a moment.";
      renderMessages();
    }
    function handleDataObj(obj) {
      if (obj && typeof obj.delta === 'string') {
        pendingText += obj.delta;
        pumpDisplay();
      }
      if (obj && obj.error) {
        throw new Error(obj.error);
      }
      if (obj && obj.done) {
        // Wait for the paced display to catch up before finalizing —
        // otherwise the bubble would jump to the full text and the
        // visitor would lose the streaming feel.
        if (paceTimer || displayedText.length < pendingText.length) {
          pendingDone = obj;
        } else {
          finalizeBot(obj);
        }
      }
    }

    fetch(url, {
      method: 'POST',
      headers: streamReady
        ? { 'Content-Type': 'application/json', 'Accept': 'text/event-stream' }
        : { 'Content-Type': 'application/json' },
      body: body,
      mode: 'cors',
      credentials: 'omit',
    }).then(function (r) {
      var ct = (r.headers.get && r.headers.get('content-type')) || '';
      if (streamReady && r.ok && r.body && r.body.getReader && ct.indexOf('text/event-stream') !== -1) {
        var reader = r.body.getReader();
        var decoder = new TextDecoder();
        var buf = '';
        function pump() {
          return reader.read().then(function (chunk) {
            if (chunk.done) {
              if (botMsg.streaming) finalizeBot({ text: botMsg.text, citations: [], handoff: false });
              return;
            }
            buf += decoder.decode(chunk.value, { stream: true });
            var events = buf.split('\n\n');
            buf = events.pop() || '';
            for (var i = 0; i < events.length; i++) {
              var lines = events[i].split('\n');
              for (var j = 0; j < lines.length; j++) {
                if (lines[j].indexOf('data: ') !== 0) continue;
                var dataStr = lines[j].slice(6);
                if (!dataStr) continue;
                try { handleDataObj(JSON.parse(dataStr)); } catch (_e) { /* skip */ }
              }
            }
            return pump();
          });
        }
        return pump();
      }
      // Fallback: non-streaming JSON path. Server returns the full
      // answer in one shot (e.g. when retrieval found 0 chunks and
      // the off-topic fallback fires before the streaming branch).
      // Type the answer out so the UX matches streamed answers —
      // otherwise visitors on KB-light tenants see most replies as
      // instant pastes and conclude the AI isn't running.
      return r.json().then(function (j) {
        var fullText = j.answer || '';
        typeOutBotMessage(botMsg, fullText, {
          onDone: function () {
            botMsg.citations = Array.isArray(j.citations) ? j.citations.slice(0, 5) : [];
            botMsg.handoff = !!j.handoff;
            pushDataLayer('qa_answer_received', { retrieved: j.retrieved || 0, citations: (botMsg.citations || []).length, handoff: botMsg.handoff });
            renderMessages();
          },
        });
      });
    }).catch(function (e) {
      console.warn('[ant-widget] convo failed:', e);
      failBot();
    }).then(function () {
      state.sending = false;
      nodes.send.disabled = false;
      nodes.input && nodes.input.focus();
    });
  }

  /**
   * Submit the inline handoff contact form. Posts to /public-form
   * with source=reachout (existing path that accepts phone OR email
   * and routes the lead via engagementStage). The conversation
   * context is bundled into the note so the team picks up the lead
   * with full visibility.
   */
  function submitHandoff(name, contact, msgIndex, extras) {
    if (!name || !contact) return;
    var payload = {
      source: 'reachout',
      name: name,
      intent: 'question',
      inv: CFG.flow,
      sourcePage: (location.host || '') + (location.pathname || ''),
      bestTime: '',
    };
    // 3-field intake: phone + email both optionally present. The
    // single-field call sites (legacy) still pass `contact` as the
    // primary; new call sites attach both via `extras`.
    if (extras && (extras.phone || extras.email)) {
      if (extras.phone) payload.phone = extras.phone;
      if (extras.email) payload.email = extras.email;
    } else if (/^[\w.+-]+@[\w-]+\.[\w.-]+$/.test(contact)) {
      payload.email = contact;
    } else {
      payload.phone = contact;
    }
    // Step context (which guide step they're on) is operational, not
    // chat content — always safe to pass. Chat history (visitor's
    // questions + bot replies) only goes through when the visitor
    // explicitly opts in via the "Share this chat..." checkbox on the
    // handoff form. Default is OFF — visitor's questions stay private
    // unless they actively choose to send them along.
    var ctx = getGuideContext();
    var noteParts = [];
    if (extras && extras.topic) noteParts.push('topic:' + extras.topic);
    if (extras && extras.message) noteParts.push('message:\n' + extras.message);
    if (ctx && ctx.stepKey) noteParts.push('on step:' + ctx.stepKey);
    var shareHistory = !!(extras && extras.shareHistory);
    if (shareHistory) {
      // Up to last 12 turns when opted in (longer context for the rep
      // than the silent 4-turn default we used to send unconditionally).
      var msgs = state.messages
        .filter(function (m) { return (m.role === 'visitor' || m.role === 'bot') && m.text; })
        .slice(-12);
      if (msgs.length) {
        var transcript = msgs.map(function (m) {
          return (m.role === 'visitor' ? 'Visitor' : 'Bot') + ': ' + (m.text || '').slice(0, 600);
        }).join('\n');
        noteParts.push('chat (visitor opted in to share):\n' + transcript);
      }
    }
    payload.shareChatHistory = shareHistory;
    if (extras && extras.topic) payload.handoffTopic = extras.topic;
    if (noteParts.length) payload.note = noteParts.join('\n---\n').slice(0, 8000);

    var bot = state.messages[msgIndex];
    if (bot) bot.handoffSending = true;
    renderMessages();

    return fetch(CFG.api + '/public-form', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
      mode: 'cors',
      credentials: 'omit',
    }).then(function (r) {
      return r.json().then(function (j) { return { status: r.status, body: j }; });
    }).then(function (resp) {
      if (resp.status >= 200 && resp.status < 300 && resp.body && resp.body.ok) {
        if (bot) {
          bot.handoffDone = true;
          bot.handoffSending = false;
        }
        pushDataLayer('qa_handoff_submitted', { lead_id: resp.body.id || null });
        renderMessages();
      } else {
        throw new Error((resp.body && resp.body.error) || 'submit failed');
      }
    }).catch(function (err) {
      if (bot) {
        bot.handoffSending = false;
        bot.handoffError = (err && err.message) || 'Try again in a moment.';
      }
      renderMessages();
    });
  }

  // ─── Share-contact-info side panel ───────────────────────────────
  //
  // A header user-plus button opens a slide-in form (Name + Email +
  // Phone) over the chat body. On submit:
  //   1) Values persist to localStorage under CFG.infoKey so future
  //      opens pre-fill the inputs (one-tap update flow).
  //   2) A POST to /public-form with source='reachout' creates the
  //      lead, identical taxonomy to the inline handoff form.
  //
  // The chat body stays mounted underneath — closing the panel
  // returns the visitor to their conversation with no message loss.
  // Streaming bot replies continue uninterrupted while the form is
  // open (the panel just covers them visually; renderMessages still
  // runs for the body underneath).

  function toggleInfoPanel() {
    if (state.infoOpen) {
      closeInfoPanel();
    } else {
      openInfoPanel();
    }
  }

  function openInfoPanel() {
    state.infoOpen = true;
    state.infoSubmitted = false;
    state.infoError = null;
    state.infoStored = loadVisitorInfo();
    if (nodes.shareInfo) {
      nodes.shareInfo.setAttribute('aria-expanded', 'true');
    }
    renderInfoPanel();
    nodes.infoPanel.classList.add('open');
    // Focus first empty field for keyboard users.
    setTimeout(function () {
      var first = nodes.infoPanel.querySelector('.antw-info-input');
      if (first) {
        try { first.focus(); } catch (e) { /* */ }
      }
    }, 60);
  }

  function closeInfoPanel() {
    state.infoOpen = false;
    if (nodes.shareInfo) {
      nodes.shareInfo.setAttribute('aria-expanded', 'false');
    }
    nodes.infoPanel.classList.remove('open');
    // Don't wipe the chat draft (user's mid-typed message). Just hand
    // focus back to the input so they can keep typing.
    setTimeout(function () { nodes.input && nodes.input.focus(); }, 60);
  }

  function renderInfoPanel() {
    if (!nodes.infoPanel) return;
    nodes.infoPanel.innerHTML = '';

    // Success state — replaces the form after a successful POST.
    if (state.infoSubmitted) {
      var ok = el('div', { class: 'antw-info-success' });
      ok.appendChild(el('div', { class: 'antw-info-success-mark' }, '✓'));
      ok.appendChild(el('div', { class: 'antw-info-success-text' },
        "Got it — your info is saved. We'll be in touch."));
      var backBtn = el('button', { class: 'antw-info-submit', type: 'button' }, 'Back to chat');
      backBtn.addEventListener('click', closeInfoPanel);
      ok.appendChild(backBtn);
      nodes.infoPanel.appendChild(ok);
      return;
    }

    var prior = state.infoStored;

    nodes.infoPanel.appendChild(el('h3', { class: 'antw-info-title' }, 'Stay in touch'));
    nodes.infoPanel.appendChild(el('p', { class: 'antw-info-sub' },
      'Drop your info — someone from the team will follow up if you want.'));
    if (prior && (prior.name || prior.email || prior.phone)) {
      var when = '';
      if (prior.submittedAt) {
        try {
          var d = new Date(prior.submittedAt);
          if (!isNaN(d.getTime())) when = ' • last shared ' + d.toLocaleDateString();
        } catch (e) { /* */ }
      }
      nodes.infoPanel.appendChild(el('div', { class: 'antw-info-prior' },
        'Already shared? Update your info' + when + '.'));
    }

    function field(labelText, type, placeholder, autocomplete, value, inputmode) {
      var wrap = el('div', { class: 'antw-info-field' });
      wrap.appendChild(el('label', { class: 'antw-info-label' }, labelText));
      var attrs = {
        class: 'antw-info-input',
        type: type,
        placeholder: placeholder,
        autocomplete: autocomplete,
      };
      if (inputmode) attrs.inputmode = inputmode;
      var inp = el('input', attrs);
      if (value) inp.value = value;
      wrap.appendChild(inp);
      return { wrap: wrap, input: inp };
    }

    var nameF  = field('Name',  'text',  'Your name',     'name',  prior && prior.name  || '');
    var emailF = field('Email', 'email', 'you@example.com','email', prior && prior.email || '', 'email');
    var phoneF = field('Phone', 'tel',   '(555) 555-5555','tel',   prior && prior.phone || '', 'tel');
    nodes.infoPanel.appendChild(nameF.wrap);
    nodes.infoPanel.appendChild(emailF.wrap);
    nodes.infoPanel.appendChild(phoneF.wrap);

    if (state.infoError) {
      nodes.infoPanel.appendChild(el('div', { class: 'antw-info-error' }, state.infoError));
    }

    var actions = el('div', { class: 'antw-info-actions' });
    var submitBtn = el('button', {
      class: 'antw-info-submit',
      type: 'button',
      disabled: state.infoSending ? 'disabled' : null,
    }, state.infoSending ? 'Sending…' : (state.infoError ? 'Retry' : 'Save'));
    submitBtn.addEventListener('click', function () {
      if (state.infoSending) return;
      var name  = (nameF.input.value  || '').trim();
      var email = (emailF.input.value || '').trim();
      var phone = (phoneF.input.value || '').trim();
      // At least one of email/phone is required (matches /public-form
      // server-side validation for source=reachout). Name is
      // encouraged but not strictly required — server treats empty
      // name as "Visitor".
      var emailOk = email && /^[\w.+-]+@[\w-]+\.[\w.-]+$/.test(email);
      var phoneOk = phone && phone.replace(/\D/g, '').length >= 7;
      if (!emailOk && !phoneOk) {
        state.infoError = 'Add an email or phone so we can reach you.';
        renderInfoPanel();
        try { (email ? emailF.input : phoneF.input).focus(); } catch (e) { /* */ }
        return;
      }
      if (email && !emailOk) {
        state.infoError = "That email doesn't look right.";
        renderInfoPanel();
        try { emailF.input.focus(); } catch (e) { /* */ }
        return;
      }
      submitInfoForm(name, emailOk ? email : '', phoneOk ? phone : '');
    });
    actions.appendChild(submitBtn);

    var cancelBtn = el('button', { class: 'antw-info-cancel', type: 'button' }, 'Back to chat');
    cancelBtn.addEventListener('click', closeInfoPanel);
    actions.appendChild(cancelBtn);
    nodes.infoPanel.appendChild(actions);
  }

  function submitInfoForm(name, email, phone) {
    state.infoSending = true;
    state.infoError = null;
    renderInfoPanel();

    var payload = {
      source: 'reachout',
      name: name || '',
      intent: 'curious',
      inv: CFG.inv || CFG.flow || '',
      sourcePage: (location.host || '') + (location.pathname || ''),
      note: 'Submitted via chat widget share-info button',
      bestTime: '',
    };
    if (email) payload.email = email;
    if (phone) payload.phone = phone;

    return fetch(CFG.api + '/public-form', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
      mode: 'cors',
      credentials: 'omit',
    }).then(function (r) {
      return r.json().then(function (j) { return { status: r.status, body: j }; });
    }).then(function (resp) {
      if (resp.status >= 200 && resp.status < 300 && resp.body && resp.body.ok) {
        state.infoSending = false;
        state.infoSubmitted = true;
        state.infoError = null;
        var saved = {
          name: name || '',
          email: email || '',
          phone: phone || '',
          submittedAt: new Date().toISOString(),
        };
        saveVisitorInfo(saved);
        state.infoStored = saved;
        // Flip the header pill into "registered" mode — label becomes
        // "Hi, <FirstName>" (or "Saved" if no name was provided), the
        // icon swaps to a checkmark, and the pill stays tappable so
        // the visitor can update their info.
        applyShareInfoState(saved);
        pushDataLayer('widget_share_info_submitted', { lead_id: resp.body.id || null });
        renderInfoPanel();
      } else {
        var msg = (resp.body && resp.body.error) || 'Could not save right now — try again in a moment.';
        throw new Error(msg);
      }
    }).catch(function (err) {
      state.infoSending = false;
      state.infoError = (err && err.message) || 'Could not save right now — try again in a moment.';
      renderInfoPanel();
    });
  }

  function enterQAMode() {
    state.qaMode = true;
    if (nodes.input) {
      nodes.input.placeholder = 'Ask about Boost, Restore, Reset…';
      setTimeout(function () { nodes.input.focus(); }, 50);
    }
    renderMessagesIfChanged();
  }

  function exitQAMode() {
    state.qaMode = false;
    if (nodes.input) nodes.input.placeholder = 'Type a message…';
  }

  function submitQA(question) {
    if (!question || state.qaSending) return;
    state.qaSending = true;
    state.sending = true;
    nodes.send.disabled = true;
    state.qaLastQuestion = question;

    // Optimistic visitor bubble for the question
    state.messages.push({ role: 'visitor', text: question });
    renderMessages();

    pushDataLayer('qa_question_asked', { question: question, flow: CFG.flow });

    fetch(CFG.api + '/qa/' + encodeURIComponent(CFG.flow) + '/ask', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ question: question }),
      mode: 'cors',
      credentials: 'omit',
    }).then(function (r) {
      return r.json().then(function (j) { return { status: r.status, body: j }; });
    }).then(function (resp) {
      var b = resp.body || {};
      var answer = b.answer || 'Sorry, I had trouble answering that. Try asking James directly.';
      var citations = Array.isArray(b.citations) ? b.citations : [];
      var refusal = isRefusal(answer);
      state.qaLastAnswer = { text: answer, citations: citations, retrieved: b.retrieved || 0, refusal: refusal };
      state.qaShownChip = false;
      pushDataLayer('qa_answer_received', { retrieved: b.retrieved || 0, citations: citations.length });
      exitQAMode();
      renderMessages();
    }).catch(function (e) {
      console.warn('[ant-widget] QA failed:', e);
      state.qaLastAnswer = { text: "Sorry — I couldn't reach the product info just now. Want me to grab James?", citations: [], retrieved: 0, refusal: true };
      state.qaShownChip = false;
      exitQAMode();
      renderMessages();
    }).then(function () {
      state.qaSending = false;
      state.sending = false;
      nodes.send.disabled = false;
      nodes.input && nodes.input.focus();
    });
  }

  // "No, talk to James" — fall back to the existing lead-chat flow with the
  // QA question pre-loaded as the visitor's first lead-chat message.
  function handoffToLeadChat() {
    var seed = state.qaLastQuestion;
    state.qaLastAnswer = null;
    state.qaShownChip = false;
    exitQAMode();

    var resume = function () {
      if (seed) sendAction({ type: 'text', message: seed });
      else renderMessagesIfChanged();
    };

    if (!state.visitorToken) {
      // No lead-chat session yet — start one, then send the seed.
      startChat().then(resume);
    } else {
      resume();
    }
  }

  function sendAction(payload) {
    if (state.sending) return;
    state.sending = true;
    nodes.send.disabled = true;
    // Once the visitor engages the lead-chat flow, the QA chip is no longer
    // an entry point — hide it. Returning to QA mode would require a reload.
    state.qaShownChip = false;
    var optimistic = null;

    if (payload.type === 'text') {
      optimistic = { role: 'visitor', text: payload.message };
    } else if (payload.type === 'pick') {
      optimistic = { role: 'visitor', text: payload.value };
    } else if (payload.type === 'submit') {
      var v = payload.value;
      optimistic = { role: 'visitor', text: [v.name, v.email, v.phone].filter(Boolean).join(' · ') };
    }
    if (optimistic) {
      state.messages.push(optimistic);
      renderMessages();
    }

    var body = { visitorToken: state.visitorToken };
    if (payload.type === 'text')   body.message = payload.message;
    else if (payload.type === 'pick' || payload.type === 'submit') body.action = { type: payload.type, value: payload.value };

    api('/lead-chat/message', 'POST', body)
      .then(function (resp) { applyResponse(resp); })
      .catch(function (e) { console.warn('[ant-widget] send failed:', e); })
      .then(function () {
        state.sending = false;
        nodes.send.disabled = false;
        nodes.input.focus();
      });
  }

  function toggleOpen() {
    state.open = !state.open;
    nodes.panel.classList.toggle('open', state.open);
    // Hide the top "Talk to <name>" CTA whenever the panel is open
    // (CSS rule `body.antw-chat-open #antw-cta{display:none;}` reads
    // this class). Synced here so every entry point — bubble, CTA,
    // close-btn, closePanel() — keeps the body class accurate.
    document.body.classList.toggle('antw-chat-open', state.open);
    var badge = document.getElementById('antw-badge');
    if (badge) { badge.textContent = ''; badge.style.display = 'none'; }
    // Closing the panel also closes the share-info form so the next
    // time the visitor opens chat they see the conversation, not a
    // stale form state. Re-opening the form is one tap on the
    // header user-plus icon.
    if (!state.open && state.infoOpen) {
      state.infoOpen = false;
      if (nodes.infoPanel) nodes.infoPanel.classList.remove('open');
      if (nodes.shareInfo) nodes.shareInfo.setAttribute('aria-expanded', 'false');
    }
    if (state.open) {
      // Initialize on first open
      if (!state.initialized) {
        state.initialized = true;
        if (CFG.ephemeral) {
          // Ephemeral conversational mode (guide popup): no lead-chat
          // init, no token, no polling. The greeting was pushed to
          // state.messages in boot(); first open just shows it.
        } else {
          // Show the "Ask about a product" chip on first open in
          // legacy mode.
          state.qaShownChip = true;
          var existing = loadToken();
          if (existing) {
            state.visitorToken = existing;
            pollConversation().then(function () {
              // If polling came back empty (token gone server-side), start fresh
              if (!state.messages || state.messages.length === 0) startChat();
            });
          } else {
            startChat();
          }
          startPolling();
        }
      }
      setTimeout(function () { nodes.input && nodes.input.focus(); }, 100);
    }
  }

  function startPolling() {
    if (state.pollTimer) return;
    state.pollTimer = setInterval(function () {
      // Only poll when chat exists and we're either open or waiting on a human
      if (!state.visitorToken) return;
      if (state.open || state.awaitingHuman) {
        pollConversation();
      }
    }, Math.max(2000, CFG.poll));
  }

  // ─── Public API ──────────────────────────────────────────────────
  function openPanel(opts) {
    if (!state.open) toggleOpen();
    if (opts && opts.intent && state.initialized) {
      // Pre-pick an intent so the visitor lands directly in that flow.
      // Wait one tick for messages to render, then auto-pick.
      setTimeout(function () {
        sendAction({ type: 'pick', value: opts.intent });
      }, 250);
    }
  }
  function closePanel() { if (state.open) toggleOpen(); }

  window.AntChat = {
    open:   openPanel,
    close:  closePanel,
    toggle: toggleOpen,
    isOpen: function () { return state.open; },
  };

  // Auto-bind clicks on any element with [data-open-chat]. Optional
  // attribute data-chat-intent="weight|dogs|build|hi" pre-picks an intent.
  function bindTriggers() {
    document.addEventListener('click', function (e) {
      var t = e.target;
      while (t && t !== document.body) {
        if (t.hasAttribute && t.hasAttribute('data-open-chat')) {
          e.preventDefault();
          var intent = t.getAttribute('data-chat-intent') || undefined;
          openPanel(intent ? { intent: intent } : undefined);
          return;
        }
        t = t.parentNode;
      }
    }, false);
  }

  // ─── Boot ────────────────────────────────────────────────────────
  /**
   * Public API on window. Lets the host page open/close the chat
   * programmatically — used by the "Get help signing up" CTAs that
   * navigate to THREE in a new tab and then immediately want the
   * chat visible on this tab without waiting for a bubble click.
   *
   * window.antChatBootGreeting (set by the host page BEFORE calling
   * antLeadChatOpen) overrides the first bot message — used by the
   * BA-help flow to say "You're on the brand ambassador page..."
   * instead of the generic ephemeral greeting.
   */
  function exposeApi() {
    if (window.antLeadChatOpen) return; // idempotent across boots
    window.antLeadChatOpen = function () {
      // Honour a host-set boot greeting once, on first open. Replace
      // the default greeting in state.messages if the chat hasn't
      // been engaged yet.
      try {
        var custom = window.antChatBootGreeting;
        if (typeof custom === 'string' && custom.trim()) {
          var noUserTurnYet = !state.messages.some(function (m) { return m.role === 'visitor'; });
          if (noUserTurnYet) {
            // Replace any existing bot greeting; keep state.messages
            // as a single-element thread so the conversation starts
            // clean.
            state.messages = [{ role: 'bot', text: custom.trim() }];
          }
          // Consume — only the first open uses it.
          window.antChatBootGreeting = null;
        }
      } catch (e) { /* */ }
      if (!state.open) toggleOpen();
    };
    window.antLeadChatClose = function () {
      if (state.open) toggleOpen();
    };
    /**
     * Open the chat with a handoff form pre-attached to the first
     * bot bubble. Used by host pages that want a one-tap "drop your
     * contact" affordance without routing through the LLM or
     * canned-intent matching.
     *
     * Args:
     *   greeting — bot text shown above the form (defaults to the
     *              tenant greeting, then a generic line).
     *   prompt   — label inside the form ("Drop your name + …").
     *   opts     — optional object:
     *     messageRequired      — show + require a message textarea
     *     messagePlaceholder   — placeholder text for the textarea
     *     topic                — short slug appended to lead note
     *                            (e.g. "service_dogs", "provider_plan")
     */
    /**
     * Run a two-step intro on the chat: stream a notice line into a
     * bot bubble (with typing animation), fire onAfterNotice when
     * the stream finishes, then after settleMs clear the conversation
     * and seed `finalGreeting` as the new opening bot message.
     *
     * Used by BA-style mobile flows that want to: (a) tell the user
     * a new tab/window is opening, (b) actually open it, then (c)
     * settle the chat into a signup-help greeting that doesn't
     * mention the now-completed transition.
     *
     * config: {
     *   notice          — text to stream first (required)
     *   finalGreeting   — replacement bot greeting after settle
     *   onAfterNotice   — fn called when notice finishes streaming
     *   settleMs        — delay after onAfterNotice before clear
     *                     (default 1500ms)
     *   chunkSize       — chars per stream tick (default 4)
     *   stepMs          — ms between ticks (default 28)
     * }
     */
    window.antChatRunIntro = function (config) {
      config = config || {};
      var notice        = (typeof config.notice === 'string' && config.notice.trim()) || '';
      var notice2       = (typeof config.notice2 === 'string' && config.notice2.trim()) || '';
      var actions       = Array.isArray(config.actions) ? config.actions : null;
      var finalGreeting = (typeof config.finalGreeting === 'string' && config.finalGreeting.trim()) || '';
      var onAfterNotice = typeof config.onAfterNotice === 'function' ? config.onAfterNotice : null;
      var settleMs      = (typeof config.settleMs === 'number') ? config.settleMs : 1500;
      var chunkSize     = (typeof config.chunkSize === 'number') ? config.chunkSize : 4;
      var stepMs        = (typeof config.stepMs    === 'number') ? config.stepMs    : 28;
      // Suppress the boot greeting — we're running our own opener.
      try { window.antChatBootGreeting = null; } catch (e) { /* */ }
      var msg = { role: 'bot', text: '' };
      state.messages = [msg];
      if (!state.open) toggleOpen();
      try { renderMessages(); } catch (_e) { /* */ }

      // Inline helper: stream a string into msg.text2 char-by-char,
      // mirroring typeOutBotMessage but for the second bubble. Used
      // by the combo-greeting flow (notice → actions → notice2).
      function streamText2(fullText, onDone) {
        msg.text2 = '';
        msg.streaming2 = true;
        renderMessages();
        if (!fullText) {
          msg.streaming2 = false;
          renderMessages();
          if (onDone) onDone();
          return;
        }
        var i = 0;
        var timer = setInterval(function () {
          i = Math.min(i + chunkSize, fullText.length);
          msg.text2 = fullText.slice(0, i);
          renderMessages();
          if (i >= fullText.length) {
            clearInterval(timer);
            msg.streaming2 = false;
            renderMessages();
            if (onDone) onDone();
          }
        }, stepMs);
      }

      typeOutBotMessage(msg, notice, {
        chunkSize: chunkSize,
        stepMs: stepMs,
        onDone: function () {
          if (onAfterNotice) {
            try { onAfterNotice(); } catch (_e) { /* */ }
          }
          // Combo flow: reveal action chips now (between the two text
          // segments), then stream notice2 into msg.text2.
          if (actions && actions.length) {
            msg.actions = actions;
            renderMessages();
          }
          if (notice2) {
            // Tiny pause before notice2 starts so the chip reveal
            // doesn't feel collapsed into the next streaming animation.
            setTimeout(function () { streamText2(notice2); }, 250);
          }
          if (finalGreeting) {
            setTimeout(function () {
              state.messages = [{ role: 'bot', text: finalGreeting }];
              try { renderMessages(); } catch (_e) { /* */ }
            }, settleMs);
          }
        }
      });
    };

    /**
     * Open the chat with a topic-picker bot bubble — bot greets, asks
     * the visitor to pick their focus. Two flavors per option:
     *
     * (1) Topical conversation (preferred): set `opener` + `actions`.
     *     Click → bot replaces the picker with a topic-specific
     *     greeting and sub-actions (sign-up, talk-to-James, etc).
     *     Visitor can then ask questions and the AI Q&A pipeline
     *     answers from the KB. Use this when the visitor likely has
     *     product/business questions before they want to be reached.
     *
     * (2) Direct handoff (legacy): set `greeting`/`prompt`/
     *     `placeholder`/`topic`/`messageRequired`. Click → contact
     *     form opens immediately. Use only when the topic implies
     *     "team must follow up directly" with no in-chat answer
     *     possible (e.g. service dogs).
     *
     * config: {
     *   greeting   — bot's opening line above the buttons
     *   options: [{
     *     label,
     *     // (1) topical conversation:
     *     opener?, actions?,
     *     // (2) direct handoff:
     *     greeting?, prompt?, placeholder?, topic?, messageRequired?
     *   }]
     * }
     */
    window.antChatPickTopic = function (config) {
      config = config || {};
      var greeting = (typeof config.greeting === 'string' && config.greeting.trim())
        || (runtimeConfig && runtimeConfig.greeting)
        || "What's your focus today?";
      var options = Array.isArray(config.options) ? config.options.filter(Boolean) : [];
      var actions = options.map(function (o) {
        // Option-level "opener" present → topical conversation flow.
        // Otherwise fall through to the legacy direct-handoff flow.
        if (typeof o.opener === 'string' && o.opener.trim()) {
          return {
            label: String(o.label || 'Continue'),
            kind: 'open-topical',
            opener: o.opener,
            topic: o.topic || null,
            actions: Array.isArray(o.actions) ? o.actions : null,
          };
        }
        return {
          label: String(o.label || 'Continue'),
          kind: 'handoff',
          greeting: o.greeting || null,
          prompt: o.prompt !== undefined ? o.prompt : null,
          placeholder: o.placeholder !== undefined ? o.placeholder : null,
          topic: o.topic || null,
          messageRequired: !!o.messageRequired,
        };
      });
      try {
        state.messages = [{
          role: 'bot',
          text: greeting.trim(),
          actions: actions,
          cannedIntent: 'page_anchor_topic_picker',
        }];
      } catch (e) { /* */ }
      if (!state.open) toggleOpen();
      try { renderMessages(); } catch (_e) { /* */ }
    };

    /**
     * Open the chat and immediately send `text` as if the visitor
     * typed it. Used by floating "ask about X" CTAs that pre-select
     * the topic — no picker, the conversation starts mid-stream with
     * the AI Q&A pipeline already responding to the question.
     *
     * Idempotent across rapid clicks: while one is in flight, further
     * calls no-op (state.sending guards the underlying sendText).
     */
    window.antChatAsk = function (text) {
      if (typeof text !== 'string' || !text.trim()) return;
      var msg = text.trim();
      function fire() {
        if (!nodes || !nodes.input) return false;
        if (!state.open) toggleOpen();
        nodes.input.value = msg;
        // Defer one tick so toggleOpen's render finishes before
        // sendText reads the input value.
        setTimeout(function () { try { sendText(); } catch (_e) { /* */ } }, 0);
        return true;
      }
      if (fire()) return;
      // Widget hasn't booted yet (defer) — short poll up to ~2.5s.
      var tries = 0;
      var poll = setInterval(function () {
        if (fire() || ++tries > 25) clearInterval(poll);
      }, 100);
    };

    window.antChatHandoff = function (greeting, prompt, opts) {
      try {
        var text = (typeof greeting === 'string' && greeting.trim())
          || (runtimeConfig && runtimeConfig.greeting)
          || 'Drop your contact and the team will reach out.';
        state.messages = [{
          role: 'bot',
          text: text,
          handoff: true,
          handoffPrompt: (typeof prompt === 'string' && prompt.trim()) || null,
          handoffMessageRequired: !!(opts && opts.messageRequired),
          handoffMessagePlaceholder: (opts && typeof opts.messagePlaceholder === 'string' && opts.messagePlaceholder) || null,
          handoffTopic: (opts && typeof opts.topic === 'string' && opts.topic) || null,
          cannedIntent: 'page_anchor_handoff',
        }];
      } catch (e) { /* */ }
      if (!state.open) toggleOpen();
      // toggleOpen on first call also triggers initial render via
      // its internal setup; force a render here so the form is
      // visible immediately even if the bubble was already open.
      try { renderMessages(); } catch (_e) { /* */ }
    };
  }

  function loadCannedIntents() {
    // Best-effort fetch of per-tenant canned intents. 404 → fall back
    // to the hardcoded matchers (no error logged). Other errors are
    // soft-logged; the chat still works on hardcoded data.
    try {
      fetch(CFG.api + '/canned-intents/' + encodeURIComponent(CFG.flow), {
        method: 'GET',
        mode: 'cors',
        credentials: 'omit',
      }).then(function (r) {
        if (r.status === 404) return null;
        if (!r.ok) throw new Error('canned-intents ' + r.status);
        return r.json();
      }).then(function (j) {
        if (!j) return;
        if (Array.isArray(j.intents) && j.intents.length && CANNED_INTENTS.setDynamic) {
          CANNED_INTENTS.setDynamic(j.intents);
        }
        // Apply per-tenant identity vars (botLabel, greeting, etc).
        applyRuntimeVars(j.variables);
        // If we already rendered the initial bot bubble (boot ran with
        // generic copy because the fetch hadn't resolved yet), upgrade
        // it in place to the tenant greeting — UNLESS the host page
        // supplied a contextual greeting via window.antChatBootGreeting
        // (e.g. "You're on the BA sign-up page"). The host-supplied
        // greeting is per-launch context and beats the generic tenant
        // default.
        if (runtimeConfig.greeting && state.messages.length === 1
            && state.messages[0].role === 'bot'
            && !state.bootGreetingFromHost
            && !state.messages.some(function (m) { return m.role === 'visitor'; })) {
          state.messages[0].text = runtimeConfig.greeting;
          renderMessages();
        }
      }).catch(function (e) { console.warn('[ant-widget] canned-intents fetch failed:', e); });
    } catch (e) { /* fetch unavailable — older browsers, tests, etc. */ }
  }

  function boot() {
    injectStyles();
    renderShell();
    bindTriggers();
    loadCannedIntents();
    // If this visitor has already shared their contact info on a
    // prior visit, flip the pill into "Hi, <FirstName>" / "Saved"
    // mode immediately on render — no flash of "Contact me" before
    // the rehydrate. The form will also pre-fill on next open.
    var prior = loadVisitorInfo();
    if (prior && (prior.name || prior.email || prior.phone)) {
      state.infoStored = prior;
      applyShareInfoState(prior);
    }
    if (CFG.ephemeral) {
      // Pure conversational LLM mode (assisted-signup guide popup).
      // No lead-chat init, no scripted bot, no token. Just an empty
      // thread with a brief greeting, ready to take the visitor's
      // first message and route every turn through /qa with full
      // history + step context.
      // Greeting precedence (first wins):
      //   1) window.antChatBootGreeting — set by the host page for
      //      one-shot context (e.g. the THREE-side popup launcher
      //      passing "You're on the BA sign-up page" with step ctx).
      //   2) runtimeConfig.greeting — per-tenant default from
      //      /canned-intents/<flow>. May not be loaded yet (fetch is
      //      async); upgraded in place by loadCannedIntents() once it
      //      resolves — UNLESS the host supplied a greeting (1), in
      //      which case the contextual greeting wins.
      //   3) generic fallback string.
      var hostGreet = (typeof window.antChatBootGreeting === 'string' && window.antChatBootGreeting.trim())
        ? window.antChatBootGreeting.trim()
        : '';
      var greet = hostGreet
        || (runtimeConfig.greeting
          || "Hey — I can help with the sign-up steps or anything about the products. What do you want to know?");
      try { window.antChatBootGreeting = null; } catch (e) { /* */ }
      // Mark whether the boot greeting came from the host page so
      // loadCannedIntents() doesn't clobber it with the generic
      // per-tenant greeting on async fetch resolve.
      state.bootGreetingFromHost = !!hostGreet;
      state.messages = [{ role: 'bot', text: greet }];
      renderMessagesIfChanged();
      exposeApi();
      // Dedicated popup window: skip the bubble entirely and open
      // straight to the conversation. The window IS the chat.
      if (CFG.fullwindow && !state.open) toggleOpen();
      return;
    }
    // Even outside ephemeral, the page may want to programmatically
    // open the chat (e.g., the "Get help signing up" CTAs on the
    // landing). Expose the open API here so it's available before
    // the user has clicked the bubble.
    exposeApi();
    // Non-ephemeral path — existing lead-chat behaviour. If there's a
    // stashed token, kick off background polling so we catch owner
    // replies even if visitor never opens the panel again.
    var existing = loadToken();
    if (existing) {
      state.visitorToken = existing;
      pollConversation();
      startPolling();
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }
})();
